import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import { showToast } from "../helper/notifications";
import { Icon } from "@iconify/react";
import { GoogleMap, MarkerF, Autocomplete } from "@react-google-maps/api";
import { useGoogleMaps } from "../context/GoogleMapsContext"; 
import PageHeader from "../components/common/PageHeader";
import LocationHistoryModal from "../components/LocationHistoryModal";
import LocationCompareModal from "../components/LocationCompareModal";
import LocationMediaExplorer from "../components/LocationMediaExplorer";
import DrawTools from "../components/DrawTools";
import FloatingFormActions from "../components/common/FloatingFormActions";
import LevelFloorplansManager from "../components/floorplans/LevelFloorplansManager";
import OutlineTemplateModal from "../components/floorplans/OutlineTemplateModal";

const DEFAULT_CENTER = { lat: 47.8238, lng: 13.0288 };

const TABS = [
  { id: "Identity", icon: "solar:shop-2-linear" },
  { id: "Address", icon: "solar:map-point-linear" },
  { id: "Building Outline", icon: "solar:buildings-2-linear" },
  { id: "Levels", icon: "solar:layers-linear" },
  { id: "Social & Web", icon: "solar:global-linear" },
  { id: "Opening Hours", icon: "solar:clock-circle-linear" },
  { id: "Parking", icon: "carbon:car" }, 
  { id: "Accessibility", icon: "carbon:accessibility" }, 
  { id: "Inclusivity", icon: "solar:heart-angle-linear" },
  { id: "Media & Files", icon: "solar:gallery-wide-linear" },
  { id: "Publishing", icon: "solar:share-circle-linear" },
  { id: "Internal IDs", icon: "solar:database-linear" },
];

const COUNTRY_CODES = [
  { code: "+49", label: "Germany (+49)" },
  { code: "+43", label: "Austria (+43)" },
  { code: "+41", label: "Switzerland (+41)" },
  { code: "+1", label: "USA / Canada (+1)" },
  { code: "+44", label: "United Kingdom (+44)" },
  { code: "+33", label: "France (+33)" },
  { code: "+39", label: "Italy (+39)" },
  { code: "+34", label: "Spain (+34)" },
  { code: "+31", label: "Netherlands (+31)" },
  { code: "+32", label: "Belgium (+32)" },
];

// ==========================================
// MAIN FORM PAGE
// ==========================================
const LocationsMainFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoaded } = useGoogleMaps();
  
  const mapRef = useRef(null);
  const addressAutocompleteRef = useRef(null);
  const poiAutocompleteRef = useRef(null);
  const outlineMapRef = useRef(null);
  const outlineFileInputRef = useRef(null);

  const [outlineDrawVersion, setOutlineDrawVersion] = useState(0);
  const [coordStep, setCoordStep] = useState(0.00001);
  const [activePolyIndex, setActivePolyIndex] = useState(0);
  const [selectedVertexIndex, setSelectedVertexIndex] = useState(null);
  const [showJsonRaw, setShowJsonRaw] = useState(false);
  const [showOutlineImport, setShowOutlineImport] = useState(false);
  const [outlineImportText, setOutlineImportText] = useState("");
  const [outlineImportMode, setOutlineImportMode] = useState("replace");
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [parentAreas, setParentAreas] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [activeTab, setActiveTab] = useState("Identity");
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);

  // Selected POI state for preview & autofill management
  const [selectedPoi, setSelectedPoi] = useState(null);
  const [poiSearchInput, setPoiSearchInput] = useState("");
  const [showCustomImport, setShowCustomImport] = useState(false);
  const [importOptions, setImportOptions] = useState({
    name: true,
    category: true,
    address: true,
    hours: true,
    contact: true,
    photos: true
  });

  // History Modal State
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  // Compare Modal State
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [isGeneratingStoreCode, setIsGeneratingStoreCode] = useState(false);
  const [storeCodeConflict, setStoreCodeConflict] = useState(null);
  const [isCheckingStoreCode, setIsCheckingStoreCode] = useState(false);

  // Gemini AI State for Public Description
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAction, setAiAction] = useState(null);
  const [previousDescription, setPreviousDescription] = useState(null);
  const [showAiMenu, setShowAiMenu] = useState(false);

  const [initialData, setInitialData] = useState(null);

  const [formData, setFormData] = useState({
    location_name: "",
    store_code: "",
    internal_id: "",
    lat: "",
    lng: "",
    street_address: "",
    house_number: "",
    postal_code: "",
    city: "",
    district: "",
    province: "",
    region: "",
    state: "",
    country: "",
    building_levels: "",
    building_levels_above_ground: "",
    building_levels_below_ground: "",
    main_entrance_level: "",
    description: "",
    opening_date: "",
    primary_category_id: "",
    additional_category: "",
    phone_country_code: "",
    phone_number: "",
    email: "",
    website_url: "",
    youtube_link: "",
    twitter_link: "",
    facebook_link: "",
    instagram_link: "",
    linkedin_link: "",
    tiktok_link: "",
    whatsapp_chat: "",
    text_message_chat: "",
    hours_monday: "", hours_tuesday: "", hours_wednesday: "", hours_thursday: "", 
    hours_friday: "", hours_saturday: "", hours_sunday: "",
    lgbtq_friendly: 0, on_site_parking: 0, free_parking_lot: 0, paid_parking_lot: 0,
    paid_street_parking: 0, appointment_required: 0, assistive_hearing_loop: 0,
    gender_neutral_toilets: 0, transgender_safe_space: 0, free_multi_storey_car_park: 0,
    paid_multi_storey_car_park: 0, wheelchair_accessible_toilet: 0, 
    free_of_charge_street_parking: 0, wheelchair_accessible_seating: 0,
    wheelchair_accessible_car_park: 0, wheelchair_accessible_entrance: 0,
    google_link: "", google_place_id: "", referring_top_location: "",
    publish_google_business: 0, publish_apple_business: 0,
    media_floorplan: "", media_photos: "", media_videos: "", media_logos: "",
    geometrics_outline: "", top_location_id: "",
    version_number: 1
  });

  const loadLocationData = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.get(`/main-locations/${id}`);
      const d = res.data.data;
      
      const loadedData = {
        ...d,
        store_code: d.store_code || "",
        phone_country_code: d.phone_country_code || "",
        lat: (d.lat !== null && d.lat !== undefined && d.lat !== "") ? parseFloat(d.lat) : "",
        lng: (d.lng !== null && d.lng !== undefined && d.lng !== "") ? parseFloat(d.lng) : "",
        lgbtq_friendly: d.lgbtq_friendly ? 1 : 0,
        on_site_parking: d.on_site_parking ? 1 : 0,
        free_parking_lot: d.free_parking_lot ? 1 : 0,
        paid_parking_lot: d.paid_parking_lot ? 1 : 0,
        paid_street_parking: d.paid_street_parking ? 1 : 0,
        appointment_required: d.appointment_required ? 1 : 0,
        assistive_hearing_loop: d.assistive_hearing_loop ? 1 : 0,
        gender_neutral_toilets: d.gender_neutral_toilets ? 1 : 0,
        transgender_safe_space: d.transgender_safe_space ? 1 : 0,
        free_multi_storey_car_park: d.free_multi_storey_car_park ? 1 : 0,
        paid_multi_storey_car_park: d.paid_multi_storey_car_park ? 1 : 0,
        wheelchair_accessible_toilet: d.wheelchair_accessible_toilet ? 1 : 0,
        free_of_charge_street_parking: d.free_of_charge_street_parking ? 1 : 0,
        wheelchair_accessible_seating: d.wheelchair_accessible_seating ? 1 : 0,
        wheelchair_accessible_car_park: d.wheelchair_accessible_car_park ? 1 : 0,
        wheelchair_accessible_entrance: d.wheelchair_accessible_entrance ? 1 : 0,
        publish_google_business: d.publish_google_business ? 1 : 0,
        publish_apple_business: d.publish_apple_business ? 1 : 0,
        building_levels: d.building_levels || "",
        building_levels_above_ground: d.building_levels_above_ground || "",
        building_levels_below_ground: d.building_levels_below_ground || "",
        main_entrance_level: d.main_entrance_level || "",
        opening_date: d.opening_date ? d.opening_date.split('T')[0] : "",
        version_number: d.version_number || 1,
        geometrics_outline: typeof d.geometrics_outline === 'object'
          ? JSON.stringify(d.geometrics_outline)
          : (d.geometrics_outline || "")
      };

      setFormData(loadedData);
      setInitialData(loadedData);
      setOutlineDrawVersion(v => v + 1);
    } catch (err) {
      showToast("error", "Failed to load location details");
    }
  }, [id]);

  useEffect(() => {
    const init = async () => {
      try {
        const [areaRes, catRes] = await Promise.all([
          api.get("/top-locations", { params: { limit: 1000 } }).catch(() => ({ data: { data: [] } })),
          api.get("/main-locations/categories").catch(() => ({ data: { data: [] } }))
        ]);
        setParentAreas(areaRes.data?.data || []);
        setCategoryOptions(catRes.data?.data || []);
        if (id) await loadLocationData();
      } catch (err) { 
        showToast("error", "Initialization failed");
      } finally { 
        setLoading(false); 
      }
    };
    init();
  }, [id, loadLocationData]);

  const resolveCategoryValue = useCallback((val) => {
    if (!val) return "";
    const match = categoryOptions.find(
      c => c.label_de === val || c.type_key === val || c.label?.toLowerCase() === val?.toLowerCase()
    );
    return match ? match.label_de : val;
  }, [categoryOptions]);

  // Selected Top Location and its preset categories
  const selectedTopLocation = useMemo(() => {
    if (!formData.top_location_id) return null;
    return parentAreas.find((a) => String(a.id) === String(formData.top_location_id)) || null;
  }, [formData.top_location_id, parentAreas]);

  const topLocationPresetCategories = useMemo(() => {
    if (!selectedTopLocation?.preset_categories) return [];
    const val = selectedTopLocation.preset_categories;
    if (Array.isArray(val)) return val;
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return typeof val === "string" ? val.split(",").map((s) => s.trim()).filter(Boolean) : [];
    }
  }, [selectedTopLocation]);

  const { presetCategories, otherCategories } = useMemo(() => {
    if (!topLocationPresetCategories || topLocationPresetCategories.length === 0) {
      return { presetCategories: [], otherCategories: categoryOptions };
    }

    const presetMatches = [];
    const others = [];

    categoryOptions.forEach((cat) => {
      const isPreset = topLocationPresetCategories.some(
        (p) =>
          p === cat.label_de ||
          p === cat.type_key ||
          (cat.label && p.toLowerCase() === cat.label.toLowerCase())
      );
      if (isPreset) {
        presetMatches.push(cat);
      } else {
        others.push(cat);
      }
    });

    return { presetCategories: presetMatches, otherCategories: others };
  }, [categoryOptions, topLocationPresetCategories]);

  // Handle Gemini AI Public Description Generation / Enhancement
  const handleAiDescription = async (action = 'generate', language = 'de', tone = 'engaging') => {
    if (action === 'generate' && !formData.location_name?.trim()) {
      showToast('error', 'Please enter a Location Name before generating a description.');
      return;
    }

    if ((action === 'polish' || action === 'shorten' || action.startsWith('translate')) && !formData.description?.trim()) {
      showToast('warning', 'Please enter or generate a description first.');
      return;
    }

    setAiLoading(true);
    setAiAction(action);
    try {
      const response = await api.post('/ai/generate-description', {
        name: formData.location_name,
        primary_category: formData.primary_category,
        secondary_category: formData.secondary_category,
        street_address: formData.street_address,
        house_number: formData.house_number,
        postal_code: formData.postal_code,
        city: formData.city,
        website: formData.website_url,
        phone: formData.phone_number,
        current_description: formData.description,
        action,
        language,
        tone
      });

      if (response.data?.success && response.data?.description) {
        setPreviousDescription(formData.description);
        setFormData(prev => ({
          ...prev,
          description: response.data.description
        }));

        const actionLabels = {
          generate: 'Description generated with Gemini!',
          polish: 'Description polished and refined!',
          shorten: 'Description condensed!',
          translate_en: 'Translated to English!',
          translate_de: 'Translated to German!'
        };
        showToast('success', actionLabels[action] || 'Description updated by Gemini AI');
      } else {
        showToast('error', response.data?.error || 'Failed to generate description');
      }
    } catch (err) {
      console.error('AI Description Generation error:', err);
      const errMsg = err.response?.data?.error || err.message || 'Error contacting Gemini AI';
      showToast('error', errMsg);
    } finally {
      setAiLoading(false);
      setAiAction(null);
    }
  };

  const hasFormChanged = () => {
    if (!initialData) return true; 
    const safeStr = (val) => (val === null || val === undefined ? "" : String(val).trim());
    
    return Object.keys(formData).some((key) => {
      if (["updated_at", "created_at", "version_number"].includes(key)) return false;
      return safeStr(formData[key]) !== safeStr(initialData[key]);
    });
  };

  const isTabCompleted = (tabId) => {
    switch (tabId) {
      case "Identity":
        return Boolean(formData.location_name && (formData.store_code || formData.primary_category_id));
      case "Address":
        return Boolean(formData.street_address || formData.city);
      case "Building Outline":
        return Boolean(formData.geometrics_outline && formData.geometrics_outline !== "[]");
      case "Levels":
        return Boolean(
          formData.building_levels ||
          formData.building_levels_above_ground ||
          formData.building_levels_below_ground ||
          formData.main_entrance_level
        );
      case "Social & Web":
        return Boolean(formData.website_url || formData.phone_number || formData.email);
      case "Opening Hours":
        return Boolean(formData.hours_monday || formData.hours_tuesday || formData.hours_wednesday || formData.hours_thursday || formData.hours_friday || formData.hours_saturday || formData.hours_sunday);
      case "Parking":
        return Boolean(formData.on_site_parking || formData.free_parking_lot || formData.paid_parking_lot || formData.paid_street_parking || formData.free_multi_storey_car_park || formData.paid_multi_storey_car_park || formData.free_of_charge_street_parking);
      case "Accessibility":
        return Boolean(formData.wheelchair_accessible_entrance || formData.wheelchair_accessible_toilet || formData.wheelchair_accessible_seating || formData.wheelchair_accessible_car_park || formData.assistive_hearing_loop);
      case "Inclusivity":
        return Boolean(formData.lgbtq_friendly || formData.gender_neutral_toilets || formData.transgender_safe_space);
      case "Media & Files":
        return Boolean(formData.media_photos || formData.media_logos || formData.media_floorplan);
      case "Internal IDs":
        return Boolean(formData.google_place_id || formData.internal_id);
      default:
        return false;
    }
  };

  const handleOpenHistory = async () => {
    setShowHistoryModal(true);
    setIsHistoryLoading(true);

    try {
      const response = await api.get(`/main-locations/${id}/history`);
      setHistoryData(response.data.data || []);
    } catch (error) {
      console.error("Error fetching version history:", error);
      showToast("error", "Failed to fetch version history");
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const onMapLoad = useCallback((map) => { mapRef.current = map; }, []);

  // Logic to focus map on existing boundaries in Building Outline tab
  const fitMapToBoundary = useCallback((outlineData) => {
    if (!outlineData || !outlineMapRef.current) return;
    try {
      const parsed = typeof outlineData === 'string' ? JSON.parse(outlineData) : outlineData;
      if (!Array.isArray(parsed)) return;

      const bounds = new window.google.maps.LatLngBounds();
      let hasCoords = false;

      parsed.forEach(item => {
        const path = Array.isArray(item) ? item : item.path;
        if (Array.isArray(path)) {
          path.forEach(pt => {
            if (pt.lat && pt.lng) {
              bounds.extend({ lat: parseFloat(pt.lat), lng: parseFloat(pt.lng) });
              hasCoords = true;
            }
          });
        }
      });

      if (hasCoords) {
        outlineMapRef.current.fitBounds(bounds, 50); 
      }
    } catch (e) {
      console.warn("Boundary zoom failed:", e);
    }
  }, []);

  const onOutlineMapLoad = useCallback((map) => {
    outlineMapRef.current = map;
    try {
      map.setTilt(0);
      map.setMapTypeId("satellite");
    } catch (e) {}
    if (formData.lat && formData.lng) {
      map.panTo({ lat: Number(formData.lat), lng: Number(formData.lng) });
      map.setZoom(18);
    }
    if (formData.geometrics_outline) {
      setTimeout(() => fitMapToBoundary(formData.geometrics_outline), 300);
    }
  }, [formData.lat, formData.lng, formData.geometrics_outline, fitMapToBoundary]);

  const handleClearBoundary = () => {
    setFormData(prev => ({ ...prev, geometrics_outline: "" }));
    setOutlineDrawVersion(v => v + 1);
  };

  const parsePolygons = useCallback((geoJson) => {
    if (!geoJson) return [];
    try {
      const parsed = typeof geoJson === "string" ? JSON.parse(geoJson) : geoJson;
      if (Array.isArray(parsed) && parsed.length && parsed[0].path) {
        return parsed.map(p => (p.path || []).map(pt => ({ lat: Number(pt.lat) || 0, lng: Number(pt.lng) || 0 })));
      }
      if (Array.isArray(parsed) && parsed.every(p => Array.isArray(p))) {
        return parsed.filter(p => p.length).map(poly => poly.map(pt => ({ lat: pt.lat !== undefined ? pt.lat : 0, lng: pt.lng !== undefined ? pt.lng : 0 })));
      }
      if (Array.isArray(parsed) && parsed.length && parsed[0].lat !== undefined) {
        return [parsed.map(pt => ({ lat: pt.lat !== undefined ? pt.lat : 0, lng: pt.lng !== undefined ? pt.lng : 0 }))];
      }
      return [];
    } catch {
      return [];
    }
  }, []);

  const parseImportedOutlineContent = useCallback((rawContent) => {
    if (!rawContent || typeof rawContent !== "string") return [];
    const trimmed = rawContent.trim();
    if (!trimmed) return [];

    const normalizeRing = (ring) => {
      if (!Array.isArray(ring)) return [];
      const pts = ring
        .map((pt) => {
          if (!pt) return null;
          if (typeof pt === "object" && !Array.isArray(pt)) {
            const lat = Number(pt.lat ?? pt.latitude);
            const lng = Number(pt.lng ?? pt.lon ?? pt.longitude);
            if (!isNaN(lat) && !isNaN(lng)) {
              return { lat: Number(lat.toFixed(7)), lng: Number(lng.toFixed(7)) };
            }
          }
          if (Array.isArray(pt) && pt.length >= 2) {
            const n0 = Number(pt[0]);
            const n1 = Number(pt[1]);
            if (!isNaN(n0) && !isNaN(n1)) {
              return { lat: Number(n0.toFixed(7)), lng: Number(n1.toFixed(7)) };
            }
          }
          return null;
        })
        .filter(Boolean);
      if (pts.length > 2) {
        const first = pts[0];
        const last = pts[pts.length - 1];
        if (Math.abs(first.lat - last.lat) < 1e-8 && Math.abs(first.lng - last.lng) < 1e-8) {
          pts.pop();
        }
      }
      return pts;
    };

    const normalizeGeoJsonRing = (ring) => {
      if (!Array.isArray(ring)) return [];
      const pts = ring
        .map((coord) => {
          if (Array.isArray(coord) && coord.length >= 2) {
            const lng = Number(coord[0]);
            const lat = Number(coord[1]);
            if (!isNaN(lat) && !isNaN(lng)) {
              return { lat: Number(lat.toFixed(7)), lng: Number(lng.toFixed(7)) };
            }
          }
          return null;
        })
        .filter(Boolean);
      if (pts.length > 2) {
        const first = pts[0];
        const last = pts[pts.length - 1];
        if (Math.abs(first.lat - last.lat) < 1e-8 && Math.abs(first.lng - last.lng) < 1e-8) {
          pts.pop();
        }
      }
      return pts;
    };

    const extractGeoJsonGeometry = (geom) => {
      if (!geom || typeof geom !== "object") return [];
      if (geom.type === "FeatureCollection" && Array.isArray(geom.features)) {
        return geom.features.flatMap((f) => extractGeoJsonGeometry(f));
      }
      if (geom.type === "Feature" && geom.geometry) {
        return extractGeoJsonGeometry(geom.geometry);
      }
      if (geom.type === "Polygon" && Array.isArray(geom.coordinates)) {
        const outer = normalizeGeoJsonRing(geom.coordinates[0]);
        return outer.length > 0 ? [outer] : [];
      }
      if (geom.type === "MultiPolygon" && Array.isArray(geom.coordinates)) {
        return geom.coordinates
          .map((polyCoords) => normalizeGeoJsonRing(polyCoords?.[0]))
          .filter((r) => r.length > 0);
      }
      if (geom.type === "LineString" && Array.isArray(geom.coordinates)) {
        const ring = normalizeGeoJsonRing(geom.coordinates);
        return ring.length > 0 ? [ring] : [];
      }
      if (geom.type === "GeometryCollection" && Array.isArray(geom.geometries)) {
        return geom.geometries.flatMap((g) => extractGeoJsonGeometry(g));
      }
      return [];
    };

    // 1. Try JSON / GeoJSON
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        if (parsed.type) {
          const geoPolys = extractGeoJsonGeometry(parsed);
          if (geoPolys.length > 0) return geoPolys;
        }
        if (Array.isArray(parsed.geometrics_outline)) {
          return parsePolygons(parsed.geometrics_outline);
        }
        if (typeof parsed.geometrics_outline === "string") {
          return parsePolygons(parsed.geometrics_outline);
        }
        if (Array.isArray(parsed.coordinates)) {
          const ring = normalizeRing(parsed.coordinates);
          if (ring.length > 0) return [ring];
        }
      }
      if (Array.isArray(parsed) && parsed.length > 0) {
        if (parsed[0]?.path && Array.isArray(parsed[0].path)) {
          return parsed.map((p) => normalizeRing(p.path)).filter((r) => r.length > 0);
        }
        if (parsed[0] && typeof parsed[0] === "object" && !Array.isArray(parsed[0])) {
          const single = normalizeRing(parsed);
          if (single.length > 0) return [single];
        }
        if (Array.isArray(parsed[0])) {
          // Could be [[{lat,lng}, ...]] or [[lat, lng], ...] or [[[lat, lng], ...]]
          if (parsed[0].length > 0 && typeof parsed[0][0] === "object" && !Array.isArray(parsed[0][0])) {
            return parsed.map((poly) => normalizeRing(poly)).filter((r) => r.length > 0);
          }
          if (typeof parsed[0][0] === "number" || typeof parsed[0][0] === "string") {
            const single = normalizeRing(parsed);
            if (single.length > 0) return [single];
          }
          if (Array.isArray(parsed[0][0])) {
            return parsed.map((poly) => normalizeRing(poly)).filter((r) => r.length > 0);
          }
        }
      }
    } catch {
      // Not valid JSON, continue to KML / text parsing
    }

    // 2. Try KML <coordinates>lng,lat[,alt] ...</coordinates>
    if (trimmed.includes("<coordinates>") || trimmed.includes("<kml")) {
      const kmlMatches = [...trimmed.matchAll(/<coordinates>([\s\S]*?)<\/coordinates>/gi)];
      if (kmlMatches.length > 0) {
        const kmlPolys = kmlMatches
          .map((m) => {
            const tupleTokens = m[1].trim().split(/\s+/);
            const ring = tupleTokens
              .map((tok) => {
                const parts = tok.split(",");
                if (parts.length >= 2) {
                  const lng = parseFloat(parts[0]);
                  const lat = parseFloat(parts[1]);
                  if (!isNaN(lat) && !isNaN(lng)) {
                    return [lng, lat];
                  }
                }
                return null;
              })
              .filter(Boolean);
            return normalizeGeoJsonRing(ring);
          })
          .filter((r) => r.length > 0);
        if (kmlPolys.length > 0) return kmlPolys;
      }
    }

    // 3. Try plain text / CSV lines of "lat, lng" or "lat; lng" or "lat lng"
    const lines = trimmed
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l && !l.startsWith("#") && !l.toLowerCase().startsWith("lat"));
    const textPoints = [];
    for (const line of lines) {
      const nums = line.match(/[-+]?\d+(?:\.\d+)?/g);
      if (nums && nums.length >= 2) {
        const a = parseFloat(nums[0]);
        const b = parseFloat(nums[1]);
        if (!isNaN(a) && !isNaN(b) && Math.abs(a) <= 90 && Math.abs(b) <= 180) {
          textPoints.push({ lat: Number(a.toFixed(7)), lng: Number(b.toFixed(7)) });
        }
      }
    }
    if (textPoints.length > 0) {
      return [textPoints];
    }

    return [];
  }, [parsePolygons]);

  const applyImportedPolygons = useCallback((importedPolys, mode = "replace") => {
    if (!importedPolys || importedPolys.length === 0) {
      showToast("error", "Could not parse valid coordinates. Please provide JSON, GeoJSON, KML, or Lat/Lng lines.");
      return false;
    }

    const existingPolys = parsePolygons(formData.geometrics_outline);
    const finalPolys = mode === "append" && existingPolys.length > 0
      ? [...existingPolys, ...importedPolys]
      : importedPolys;

    const serialized = JSON.stringify(finalPolys);
    setFormData((prev) => ({
      ...prev,
      geometrics_outline: serialized
    }));
    const targetPolyIdx = mode === "append" && existingPolys.length > 0 ? existingPolys.length : 0;
    setActivePolyIndex(targetPolyIdx);
    setSelectedVertexIndex(0);
    setOutlineDrawVersion((v) => v + 1);
    setTimeout(() => fitMapToBoundary(serialized), 150);

    const totalPts = importedPolys.reduce((acc, p) => acc + p.length, 0);
    showToast(
      "success",
      `Imported ${importedPolys.length} ${importedPolys.length === 1 ? "shape" : "shapes"} (${totalPts} vertices)!`
    );
    return true;
  }, [formData.geometrics_outline, parsePolygons, fitMapToBoundary]);

  const handleOutlineTextImport = () => {
    const polys = parseImportedOutlineContent(outlineImportText);
    if (applyImportedPolygons(polys, outlineImportMode)) {
      setOutlineImportText("");
      setShowOutlineImport(false);
    }
  };

  const handleOutlineFileImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const content = String(evt.target?.result || "");
      const polys = parseImportedOutlineContent(content);
      if (applyImportedPolygons(polys, outlineImportMode)) {
        setOutlineImportText("");
        setShowOutlineImport(false);
      } else {
        setOutlineImportText(content);
      }
    };
    reader.onerror = () => {
      showToast("error", "Failed to read file.");
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleCoordinateChange = (polyIdx, ptIdx, field, val) => {
    const polys = parsePolygons(formData.geometrics_outline);
    if (!polys[polyIdx] || !polys[polyIdx][ptIdx]) return;

    const updatedPolys = polys.map((poly, pIdx) => {
      if (pIdx !== polyIdx) return poly;
      return poly.map((pt, idx) => {
        if (idx !== ptIdx) return pt;
        return {
          ...pt,
          [field]: val
        };
      });
    });

    setFormData(prev => ({
      ...prev,
      geometrics_outline: JSON.stringify(updatedPolys)
    }));
  };

  const handleCoordinateBlur = (polyIdx, ptIdx, field) => {
    const polys = parsePolygons(formData.geometrics_outline);
    if (!polys[polyIdx] || !polys[polyIdx][ptIdx]) return;

    const rawVal = polys[polyIdx][ptIdx][field];
    const num = parseFloat(rawVal);
    if (!isNaN(num)) {
      const updatedPolys = polys.map((poly, pIdx) => {
        if (pIdx !== polyIdx) return poly;
        return poly.map((pt, idx) => {
          if (idx !== ptIdx) return pt;
          return {
            ...pt,
            [field]: Number(num.toFixed(7))
          };
        });
      });
      setFormData(prev => ({
        ...prev,
        geometrics_outline: JSON.stringify(updatedPolys)
      }));
    }
  };

  const adjustCoordinate = (polyIdx, ptIdx, field, delta) => {
    const polys = parsePolygons(formData.geometrics_outline);
    if (!polys[polyIdx] || !polys[polyIdx][ptIdx]) return;

    const currentVal = parseFloat(polys[polyIdx][ptIdx][field]) || 0;
    const newVal = Number((currentVal + delta).toFixed(7));

    const updatedPolys = polys.map((poly, pIdx) => {
      if (pIdx !== polyIdx) return poly;
      return poly.map((pt, idx) => {
        if (idx !== ptIdx) return pt;
        return {
          ...pt,
          [field]: newVal
        };
      });
    });

    setFormData(prev => ({
      ...prev,
      geometrics_outline: JSON.stringify(updatedPolys)
    }));
  };

  const handleRemovePoint = (polyIdx, ptIdx) => {
    const polys = parsePolygons(formData.geometrics_outline);
    if (!polys[polyIdx]) return;

    const updatedPath = polys[polyIdx].filter((_, idx) => idx !== ptIdx);
    let updatedPolys;
    if (updatedPath.length === 0) {
      updatedPolys = polys.filter((_, idx) => idx !== polyIdx);
    } else {
      updatedPolys = polys.map((p, idx) => idx === polyIdx ? updatedPath : p);
    }

    setFormData(prev => ({
      ...prev,
      geometrics_outline: updatedPolys.length > 0 ? JSON.stringify(updatedPolys) : ""
    }));
  };

  const handleAddPoint = (polyIdx = 0) => {
    const polys = parsePolygons(formData.geometrics_outline);
    let updatedPolys;

    if (polys.length === 0) {
      const centerLat = Number(formData.lat) || DEFAULT_CENTER.lat;
      const centerLng = Number(formData.lng) || DEFAULT_CENTER.lng;
      const delta = 0.00015; // ~15m starter square
      const initialBox = [
        { lat: Number((centerLat + delta).toFixed(7)), lng: Number((centerLng - delta).toFixed(7)) },
        { lat: Number((centerLat + delta).toFixed(7)), lng: Number((centerLng + delta).toFixed(7)) },
        { lat: Number((centerLat - delta).toFixed(7)), lng: Number((centerLng + delta).toFixed(7)) },
        { lat: Number((centerLat - delta).toFixed(7)), lng: Number((centerLng - delta).toFixed(7)) }
      ];
      updatedPolys = [initialBox];
    } else {
      const targetPoly = polys[polyIdx] || polys[0];
      if (targetPoly.length === 0) {
        const centerLat = Number(formData.lat) || DEFAULT_CENTER.lat;
        const centerLng = Number(formData.lng) || DEFAULT_CENTER.lng;
        const newPt = { lat: Number(centerLat.toFixed(7)), lng: Number(centerLng.toFixed(7)) };
        updatedPolys = polys.map((p, idx) => idx === polyIdx ? [newPt] : p);
      } else {
        const lastPt = targetPoly[targetPoly.length - 1];
        const newPt = {
          lat: Number((Number(lastPt.lat) + 0.00003).toFixed(7)),
          lng: Number((Number(lastPt.lng) + 0.00003).toFixed(7))
        };
        updatedPolys = polys.map((p, idx) => idx === polyIdx ? [...p, newPt] : p);
      }
    }

    setFormData(prev => ({
      ...prev,
      geometrics_outline: JSON.stringify(updatedPolys)
    }));
  };

  // Add an additional shape (polygon)
  const handleAddShape = () => {
    const polys = parsePolygons(formData.geometrics_outline);

    let centerLat = Number(formData.lat) || DEFAULT_CENTER.lat;
    let centerLng = Number(formData.lng) || DEFAULT_CENTER.lng;

    if (outlineMapRef.current?.getCenter) {
      const c = outlineMapRef.current.getCenter();
      if (c) {
        centerLat = c.lat();
        centerLng = c.lng();
      }
    }

    const offset = polys.length * 0.0003;
    const delta = 0.00015; // ~15m starter square

    const newBox = [
      { lat: Number((centerLat + offset + delta).toFixed(7)), lng: Number((centerLng + offset - delta).toFixed(7)) },
      { lat: Number((centerLat + offset + delta).toFixed(7)), lng: Number((centerLng + offset + delta).toFixed(7)) },
      { lat: Number((centerLat + offset - delta).toFixed(7)), lng: Number((centerLng + offset + delta).toFixed(7)) },
      { lat: Number((centerLat + offset - delta).toFixed(7)), lng: Number((centerLng + offset - delta).toFixed(7)) }
    ];

    const updatedPolys = [...polys, newBox];
    const newIndex = updatedPolys.length - 1;

    setFormData((prev) => ({
      ...prev,
      geometrics_outline: JSON.stringify(updatedPolys)
    }));
    setActivePolyIndex(newIndex);
    setSelectedVertexIndex(0);
    setOutlineDrawVersion((v) => v + 1);

    if (outlineMapRef.current) {
      outlineMapRef.current.panTo({ lat: centerLat + offset, lng: centerLng + offset });
    }
  };

  useEffect(() => {
    if (activeTab === "Building Outline" && outlineMapRef.current) {
      window.google?.maps?.event?.trigger(outlineMapRef.current, "resize");
      try {
        outlineMapRef.current.setTilt(0);
        outlineMapRef.current.setMapTypeId("satellite");
      } catch (e) {}
      if (formData.geometrics_outline) {
        setTimeout(() => fitMapToBoundary(formData.geometrics_outline), 150);
      } else if (formData.lat && formData.lng) {
        outlineMapRef.current.panTo({ lat: Number(formData.lat), lng: Number(formData.lng) });
        outlineMapRef.current.setZoom(18);
      }
    }
  }, [activeTab, fitMapToBoundary]);

  const handleAutoGenerateStoreCode = async () => {
    setIsGeneratingStoreCode(true);
    try {
      const res = await api.get("/main-locations/generate-store-code", {
        params: {
          country: formData.country,
          top_location_id: formData.top_location_id,
          location_name: formData.location_name,
          exclude_id: id || ""
        }
      });
      if (res.data?.store_code) {
        setFormData(prev => ({ ...prev, store_code: res.data.store_code }));
        setStoreCodeConflict(null);
        showToast("success", `Generated Store Code: ${res.data.store_code}`);
      }
    } catch (err) {
      console.error("Failed to generate store code:", err);
      showToast("error", "Failed to auto-generate store code.");
    } finally {
      setIsGeneratingStoreCode(false);
    }
  };

  // Real-time / debounced check for duplicate store_code
  useEffect(() => {
    const trimmed = String(formData.store_code || "").trim();
    if (!trimmed) {
      setStoreCodeConflict(null);
      setIsCheckingStoreCode(false);
      return;
    }

    // If editing an existing location and the store_code matches the initial saved value,still verify in case another record shares it, or debounce 300ms
    let cancelled = false;
    setIsCheckingStoreCode(true);

    const timer = setTimeout(async () => {
      try {
        const res = await api.get("/main-locations/check-store-code", {
          params: {
            store_code: trimmed,
            exclude_id: id || ""
          }
        });
        if (!cancelled) {
          if (res.data?.exists) {
            setStoreCodeConflict(res.data);
          } else {
            setStoreCodeConflict(null);
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Store code check failed:", err);
        }
      } finally {
        if (!cancelled) {
          setIsCheckingStoreCode(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [formData.store_code, id]);

  const handleApplyStoreCodeSuggestion = (suggestedCode) => {
    if (!suggestedCode) return;
    setFormData(prev => ({ ...prev, store_code: suggestedCode }));
    setStoreCodeConflict(null);
    showToast("success", `Applied suggested Store Code: ${suggestedCode}`);
  };

  // Helper to parse address components
  const parseAddressComponents = (components = []) => {
    const getComp = (type) => components.find(c => c.types?.includes(type));
    return {
      street_number: getComp('street_number')?.long_name || '',
      route: getComp('route')?.long_name || '',
      city: getComp('locality')?.long_name || getComp('postal_town')?.long_name || getComp('sublocality_level_1')?.long_name || '',
      district: getComp('sublocality')?.long_name || getComp('administrative_area_level_2')?.long_name || '',
      state: getComp('administrative_area_level_1')?.long_name || '',
      postal_code: getComp('postal_code')?.long_name || '',
      country: getComp('country')?.long_name || ''
    };
  };

  // Helper to parse opening hours weekday_text
  const parseOpeningHours = (openingHours) => {
    const result = {};
    if (!openingHours?.weekday_text || !Array.isArray(openingHours.weekday_text)) return result;
    
    const dayMap = [
      { key: 'monday', field: 'hours_monday', aliases: ['monday', 'montag'] },
      { key: 'tuesday', field: 'hours_tuesday', aliases: ['tuesday', 'dienstag'] },
      { key: 'wednesday', field: 'hours_wednesday', aliases: ['wednesday', 'mittwoch'] },
      { key: 'thursday', field: 'hours_thursday', aliases: ['thursday', 'donnerstag'] },
      { key: 'friday', field: 'hours_friday', aliases: ['friday', 'freitag'] },
      { key: 'saturday', field: 'hours_saturday', aliases: ['saturday', 'samstag'] },
      { key: 'sunday', field: 'hours_sunday', aliases: ['sunday', 'sonntag'] }
    ];

    openingHours.weekday_text.forEach(item => {
      const parts = item.split(/:\s*(.+)/);
      if (parts.length >= 2) {
        const dayLabel = parts[0].trim().toLowerCase();
        const hoursText = parts[1].trim();
        const matched = dayMap.find(d => d.aliases.some(alias => dayLabel.startsWith(alias)));
        if (matched) {
          result[matched.field] = hoursText;
        }
      }
    });
    return result;
  };

  // Handle POI selection from Google Places Autocomplete dropdown
  const onPoiSelected = () => {
    const place = poiAutocompleteRef.current?.getPlace();
    if (!place || !place.geometry) {
      showToast("warning", "Please select a Point of Interest from the dropdown list.");
      return;
    }

    const addr = parseAddressComponents(place.address_components);
    const parsedHours = parseOpeningHours(place.opening_hours);
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    // Readable category mapped to German if available
    const cleanTypes = (place.types || []).filter(t => !['establishment', 'point_of_interest'].includes(t));
    const rawType = cleanTypes[0] || place.types?.[0] || '';
    const matchedCat = categoryOptions.find(c => c.type_key === rawType || c.type_key === rawType.replace(/\s+/g, '_').toLowerCase());
    const primaryCat = matchedCat ? matchedCat.label_de : (rawType ? rawType.replace(/_/g, ' ') : '');

    const rawSecType = cleanTypes[1] || '';
    const matchedSecCat = categoryOptions.find(c => c.type_key === rawSecType || c.type_key === rawSecType.replace(/\s+/g, '_').toLowerCase());
    const secondaryCat = matchedSecCat ? matchedSecCat.label_de : '';

    // Photos URLs
    let photosPayload = '';
    if (place.photos && place.photos.length > 0) {
      try {
        const photoUrls = place.photos.slice(0, 5).map(p => {
          if (typeof p.getUrl === 'function') {
            return p.getUrl({ maxWidth: 1200 });
          }
          return '';
        }).filter(Boolean);
        if (photoUrls.length > 0) {
          photosPayload = JSON.stringify(photoUrls);
        }
      } catch (err) {
        console.warn('Could not extract photo URLs:', err);
      }
    }

    const poiData = {
      name: place.name || '',
      formatted_address: place.formatted_address || '',
      lat,
      lng,
      street_address: addr.route || place.name || '',
      house_number: addr.street_number || '',
      postal_code: addr.postal_code || '',
      city: addr.city || '',
      district: addr.district || '',
      state: addr.state || '',
      country: addr.country || '',
      phone_number: place.formatted_phone_number || place.international_phone_number || '',
      website_url: place.website || '',
      google_place_id: place.place_id || '',
      google_link: place.url || (place.place_id ? `https://www.google.com/maps/place/?q=place_id:${place.place_id}` : ''),
      primary_category_id: primaryCat,
      additional_category: secondaryCat,
      rating: place.rating || null,
      user_ratings_total: place.user_ratings_total || 0,
      hours: parsedHours,
      photos: photosPayload
    };

    setSelectedPoi(poiData);
    setPoiSearchInput(place.name || place.formatted_address || "");
    applyPoiAutofill(poiData, importOptions);
  };

  const applyPoiAutofill = (poi, opts = importOptions) => {
    if (!poi) return;
    const newPos = { lat: Number(poi.lat), lng: Number(poi.lng) };

    setFormData(prev => ({
      ...prev,
      ...(opts.name ? { location_name: poi.name || prev.location_name } : {}),
      ...(opts.category ? { 
        primary_category_id: poi.primary_category_id || prev.primary_category_id,
        additional_category: poi.additional_category || prev.additional_category
      } : {}),
      ...(opts.address ? {
        lat: poi.lat,
        lng: poi.lng,
        street_address: poi.street_address || prev.street_address,
        house_number: poi.house_number || prev.house_number,
        postal_code: poi.postal_code || prev.postal_code,
        city: poi.city || prev.city,
        district: poi.district || prev.district,
        state: poi.state || prev.state,
        country: poi.country || prev.country,
      } : {}),
      ...(opts.contact ? {
        phone_number: poi.phone_number || prev.phone_number,
        website_url: poi.website_url || prev.website_url,
      } : {}),
      ...(opts.photos && poi.photos ? { media_photos: poi.photos } : {}),
      ...(opts.hours && poi.hours ? poi.hours : {}),
      google_place_id: poi.google_place_id || prev.google_place_id,
      google_link: poi.google_link || prev.google_link,
    }));

    if (mapRef.current && poi.lat && poi.lng && opts.address) {
      mapRef.current.panTo(newPos);
      mapRef.current.setZoom(16);
    }

    showToast("success", `Applied fields from POI "${poi.name}"!`);
  };

  const clearSelectedPoi = () => {
    setSelectedPoi(null);
    setPoiSearchInput("");
  };

  const onAddressChanged = () => {
    const place = addressAutocompleteRef.current?.getPlace();
    if (!place?.geometry) return;
    const newPos = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
    const addr = parseAddressComponents(place.address_components);
    
    setFormData(p => ({ 
      ...p, 
      lat: newPos.lat, 
      lng: newPos.lng, 
      street_address: addr.route || place.name || p.street_address,
      house_number: addr.street_number || p.house_number,
      postal_code: addr.postal_code || p.postal_code,
      city: addr.city || p.city,
      district: addr.district || p.district,
      state: addr.state || p.state,
      country: addr.country || p.country
    }));
    mapRef.current?.panTo(newPos);
    mapRef.current?.setZoom(16);
    showToast("info", "Address updated from search");
  };

  const handleMarkerDragEnd = (e) => {
    const newLat = e.latLng.lat();
    const newLng = e.latLng.lng();
    setFormData(prev => ({ ...prev, lat: newLat, lng: newLng }));

    if (window.google?.maps?.Geocoder) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat: newLat, lng: newLng } }, (results, status) => {
        if (status === "OK" && results?.[0]) {
          const addr = parseAddressComponents(results[0].address_components);
          setFormData(prev => ({
            ...prev,
            street_address: addr.route || prev.street_address,
            house_number: addr.street_number || prev.house_number,
            postal_code: addr.postal_code || prev.postal_code,
            city: addr.city || prev.city,
            district: addr.district || prev.district,
            state: addr.state || prev.state,
            country: addr.country || prev.country,
          }));
          showToast("info", "Address updated from pin position!");
        }
      });
    }
  };

  const applyHoursPreset = (preset) => {
    if (preset === 'office') {
      setFormData(prev => ({
        ...prev,
        hours_monday: "09:00 - 18:00",
        hours_tuesday: "09:00 - 18:00",
        hours_wednesday: "09:00 - 18:00",
        hours_thursday: "09:00 - 18:00",
        hours_friday: "09:00 - 18:00",
        hours_saturday: "Closed",
        hours_sunday: "Closed",
      }));
      showToast("info", "Set standard hours (Mon-Fri 09:00-18:00, Sat-Sun Closed)");
    } else if (preset === 'retail') {
      setFormData(prev => ({
        ...prev,
        hours_monday: "10:00 - 20:00",
        hours_tuesday: "10:00 - 20:00",
        hours_wednesday: "10:00 - 20:00",
        hours_thursday: "10:00 - 20:00",
        hours_friday: "10:00 - 20:00",
        hours_saturday: "10:00 - 20:00",
        hours_sunday: "Closed",
      }));
      showToast("info", "Set retail hours (Mon-Sat 10:00-20:00)");
    } else if (preset === '247') {
      setFormData(prev => ({
        ...prev,
        hours_monday: "Open 24 hours",
        hours_tuesday: "Open 24 hours",
        hours_wednesday: "Open 24 hours",
        hours_thursday: "Open 24 hours",
        hours_friday: "Open 24 hours",
        hours_saturday: "Open 24 hours",
        hours_sunday: "Open 24 hours",
      }));
      showToast("info", "Set 24/7 opening hours");
    } else if (preset === 'copy_monday') {
      const mon = formData.hours_monday || "09:00 - 18:00";
      setFormData(prev => ({
        ...prev,
        hours_tuesday: mon,
        hours_wednesday: mon,
        hours_thursday: mon,
        hours_friday: mon,
      }));
      showToast("info", "Copied Monday hours to all weekdays (Tue-Fri)");
    } else if (preset === 'clear') {
      setFormData(prev => ({
        ...prev,
        hours_monday: "",
        hours_tuesday: "",
        hours_wednesday: "",
        hours_thursday: "",
        hours_friday: "",
        hours_saturday: "",
        hours_sunday: "",
      }));
      showToast("info", "Cleared all opening hours");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value 
    }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (id && !hasFormChanged()) {
      showToast("info", "No changes detected in any field.");
      return;
    }

    setSaveLoading(true); 

    try {
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

      let currentStoreCode = formData.store_code ? String(formData.store_code).trim() : '';
      if (!currentStoreCode) {
        try {
          const codeRes = await api.get("/main-locations/generate-store-code", {
            params: {
              country: formData.country,
              top_location_id: formData.top_location_id,
              location_name: formData.location_name,
              exclude_id: id || ""
            }
          });
          if (codeRes.data?.store_code) {
            currentStoreCode = codeRes.data.store_code;
            setFormData(prev => ({ ...prev, store_code: currentStoreCode }));
          }
        } catch (e) {
          // Fallback handled by backend
        }
      } else {
        // Verify uniqueness of entered store_code
        const checkRes = await api.get("/main-locations/check-store-code", {
          params: {
            store_code: currentStoreCode,
            exclude_id: id || ""
          }
        });
        if (checkRes.data?.exists) {
          setStoreCodeConflict(checkRes.data);
          const conflictLocName = checkRes.data.conflictingLocation?.location_name || `ID #${checkRes.data.conflictingLocation?.location_id}`;
          showToast(
            "error",
            `Store code "${currentStoreCode}" already exists (used by "${conflictLocName}"). Suggested code: ${checkRes.data.suggestion}`
          );
          if (activeTab !== "Identity" && activeTab !== "Internal IDs") {
            setActiveTab("Identity");
          }
          setSaveLoading(false);
          return;
        }
      }

      const payload = { 
        ...formData,
        store_code: currentStoreCode || formData.store_code,
        user_id: currentUser.id || null
      };
      
      if (id) {
        await api.put(`/main-locations/${id}`, payload);
        const successMsg = activeTab === "Building Outline"
          ? "Building outline saved successfully"
          : activeTab === "Levels"
          ? "Levels and floorplans saved successfully"
          : "Location profile saved successfully";
        showToast("success", successMsg);
        await loadLocationData(); 
      } else {
        const response = await api.post("/main-locations", payload);
        const successMsg = activeTab === "Building Outline"
          ? "Building outline created successfully"
          : activeTab === "Levels"
          ? "Levels and floorplans created successfully"
          : "Location created successfully";
        showToast("success", successMsg);
        const newLocationId = response.data.location_id; 
        navigate(`/locations-main/${newLocationId}/edit`); 
      }
    } catch (error) { 
      console.error("Save error:", error);
      const errData = error.response?.data;
      if (error.response?.status === 409 || errData?.code === "DUPLICATE_STORE_CODE") {
        setStoreCodeConflict({
          exists: true,
          conflictingLocation: errData.conflictingLocation,
          suggestion: errData.suggestion
        });
        showToast("error", errData.message || "Store code already exists.");
        if (activeTab !== "Identity" && activeTab !== "Internal IDs") {
          setActiveTab("Identity");
        }
      } else {
        showToast("error", errData?.message || "Failed to save record");
      }
    } finally {
      setSaveLoading(false); 
    }
  };

  const handleSaveOutline = async (e) => {
    if (e) e.preventDefault();
    await handleSubmit(e);
  };

  const handleRestoreVersion = async (versionNumber) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

      await api.post(`/main-locations/${id}/restore/${versionNumber}`, {
        user_id: currentUser.id
      });

      showToast("success", `Restored version ${versionNumber} successfully!`);
      await loadLocationData(); 
      setShowHistoryModal(false);
    } catch (err) {
      console.error("Restore failed:", err);
      showToast("error", "Failed to restore historical version.");
    }
  };

  const tabIndex = TABS.findIndex(t => t.id === activeTab);
  const prevTab = tabIndex > 0 ? TABS[tabIndex - 1].id : null;
  const nextTab = tabIndex < TABS.length - 1 ? TABS[tabIndex + 1].id : null;

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center py-80 bg-base radius-12 shadow-sm">
      <Icon icon="line-md:loading-twotone-loop" className="text-primary-600 text-4xl" />
    </div>
  );

  return (
    <>
      {/* CLEAN BACK BUTTON */}
      <button
        type="button"
        onClick={() => navigate("/locations-main")}
        className="btn btn-link p-0 text-secondary-light text-decoration-none d-inline-flex align-items-center gap-2 mb-16 hover-text-primary-600 transition-all"
      >
        <Icon icon="solar:arrow-left-linear" className="text-xl" />
        <span className="fw-medium text-sm">Back to Locations</span>
      </button>

      {/* HEADER SECTION WITH HISTORY BUTTON */}
      <PageHeader
        title={id ? `Edit Location: ${formData.location_name || "Profile"}` : "Add New Location"}
        actions={
          <div className="d-flex align-items-center gap-2">
            {formData.google_link && (
              <a
                href={formData.google_link}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-secondary d-inline-flex align-items-center gap-2 px-16 py-10 radius-8 fw-semibold"
              >
                <Icon icon="solar:map-arrow-square-linear" className="text-xl" />
                <span className="text-sm">Maps</span>
              </a>
            )}
            {id && (
              <button
                type="button"
                onClick={() => setShowCompareModal(true)}
                className="btn btn-outline-info d-inline-flex align-items-center gap-2 px-16 py-10 radius-8 fw-semibold"
              >
                <Icon icon="solar:scale-bold" className="text-xl" />
                <span className="text-sm">Compare</span>
              </button>
            )}
            {id && (
              <button
                type="button"
                onClick={handleOpenHistory}
                className="btn btn-outline-primary d-inline-flex align-items-center gap-2 px-16 py-10 radius-8 fw-semibold"
              >
                <Icon icon="solar:history-linear" className="text-xl" />
                <span className="text-sm">History</span>
              </button>
            )}
          </div>
        }
      />

      <form onSubmit={handleSubmit} className="mb-80">
        <div className="card radius-12 border-0 shadow-none bg-base overflow-hidden">
          <div className="d-flex flex-column flex-lg-row">
            
            {/* NAVIGATION SIDEBAR WITH COMPLETION BADGES */}
            <div className="bg-neutral-50 border-end" style={{ minWidth: "260px" }}>
              <div className="p-16">
                <div className="d-flex flex-column gap-1">
                  {TABS.map((tab) => {
                    const completed = isTabCompleted(tab.id);
                    return (
                      <button 
                        key={tab.id} 
                        type="button" 
                        onClick={() => setActiveTab(tab.id)}
                        className={`d-flex align-items-center justify-content-between px-16 py-12 radius-8 border-0 transition-all text-start w-100 ${
                          activeTab === tab.id ? "bg-primary-600 text-white shadow-sm fw-bold" : "bg-transparent text-secondary-light hover-bg-neutral-200"
                        }`}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <Icon icon={tab.icon} className={`text-xl ${activeTab === tab.id ? "text-white" : "text-primary-600"}`} />
                          <span className="text-sm">{tab.id}</span>
                        </div>
                        {completed && (
                          <span 
                            className={`badge ${activeTab === tab.id ? "bg-white text-primary-600" : "bg-success-50 text-success-600 border border-success-200"} radius-circle p-4`}
                            title="Filled"
                          >
                            <Icon icon="solar:check-circle-bold" className="text-xs" />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* FORM CONTENT */}
            <div className="flex-grow-1 p-24 p-lg-36" style={{ minHeight: "70vh" }}>
              <div className="d-flex align-items-center justify-content-between mb-28 border-bottom pb-16">
                <h5 className="fw-bold text-primary-light mb-0">{activeTab}</h5>
                <span className="text-xs text-secondary-light">
                  Section {tabIndex + 1} of {TABS.length}
                </span>
              </div>

              {/* TAB 1: IDENTITY */}
              {activeTab === "Identity" && (
                <div className="row g-4">
                  
                  {/* INTEGRATED GOOGLE PLACES POI QUICK IMPORT CARD (Only for new locations) */}
                  {!id && (
                    <div className="col-12">
                      <div className="p-20 radius-12 bg-primary-50 border border-primary-200">
                      <div className="d-flex align-items-start gap-3 mb-16">
                        <div className="w-40-px h-40-px bg-primary-600 text-white radius-8 d-flex align-items-center justify-content-center flex-shrink-0 shadow-xs">
                          <Icon icon="solar:shop-2-bold" className="text-2xl" />
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <h6 className="fw-bold text-primary-light mb-0">Google Places POI Quick-Import</h6>
                            <span className="badge bg-white text-primary-600 border border-primary-200 px-8 py-3 radius-4 text-xs fw-semibold">
                              Smart Autofill
                            </span>
                          </div>
                          <p className="text-xs text-secondary-light mb-0 mt-2">
                            Search any Point of Interest to automatically fill Name, Address, Category, Hours, Contact & Place IDs.
                          </p>
                        </div>
                      </div>

                      {/* Autocomplete Input */}
                      {isLoaded ? (
                        <Autocomplete
                          onLoad={(autocomplete) => {
                            poiAutocompleteRef.current = autocomplete;
                          }}
                          onPlaceChanged={onPoiSelected}
                          options={{
                            types: ['establishment'],
                            fields: [
                              'name',
                              'formatted_address',
                              'address_components',
                              'geometry',
                              'place_id',
                              'formatted_phone_number',
                              'international_phone_number',
                              'website',
                              'opening_hours',
                              'photos',
                              'url',
                              'types',
                              'rating',
                              'user_ratings_total'
                            ]
                          }}
                        >
                          <div className="position-relative">
                            <span className="position-absolute top-50 start-0 translate-middle-y ms-16 text-primary-600 text-xl pointer-events-none d-flex align-items-center">
                              <Icon icon="solar:magnifer-linear" />
                            </span>
                            <input
                              type="text"
                              className="form-control form-control-lg radius-8 ps-48 pe-40 bg-white border-neutral-300 focus-border-primary-600 shadow-none text-sm"
                              placeholder="Search location or POI..."
                              value={poiSearchInput}
                              onChange={(e) => setPoiSearchInput(e.target.value)}
                            />
                            {poiSearchInput && (
                              <button
                                type="button"
                                onClick={clearSelectedPoi}
                                className="btn p-0 border-0 position-absolute top-50 end-0 translate-middle-y me-16 text-secondary-light hover-text-danger d-flex align-items-center"
                                title="Clear"
                              >
                                <Icon icon="solar:close-circle-bold" className="text-xl" />
                              </button>
                            )}
                          </div>
                        </Autocomplete>
                      ) : (
                        <div className="form-control form-control-lg bg-neutral-100 text-secondary-light text-sm radius-8 d-flex align-items-center gap-2">
                          <Icon icon="line-md:loading-twotone-loop" />
                          <span>Loading Places API...</span>
                        </div>
                      )}

                      {/* POI Preview & Field Selectors */}
                      {selectedPoi && (
                        <div className="mt-16 p-16 radius-8 bg-white border border-primary-300 shadow-xs">
                          <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3">
                            <div>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span className="fw-bold text-primary-light text-base">{selectedPoi.name}</span>
                                {selectedPoi.primary_category_id && (
                                  <span className="badge bg-neutral-100 text-secondary-light text-xs text-capitalize border">
                                    {selectedPoi.primary_category_id}
                                  </span>
                                )}
                                {selectedPoi.rating && (
                                  <span className="badge bg-warning-50 text-warning-600 border border-warning-200 text-xs d-flex align-items-center gap-1">
                                    ⭐ {selectedPoi.rating} ({selectedPoi.user_ratings_total})
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-secondary-light mt-6 d-flex flex-wrap gap-3">
                                <span>📍 {selectedPoi.formatted_address || `${selectedPoi.city}, ${selectedPoi.country}`}</span>
                                {selectedPoi.phone_number && <span>📞 {selectedPoi.phone_number}</span>}
                                {selectedPoi.website_url && (
                                  <a href={selectedPoi.website_url} target="_blank" rel="noopener noreferrer" className="text-primary-600 text-decoration-none fw-medium">
                                    🌐 Website
                                  </a>
                                )}
                              </div>
                            </div>

                            <div className="d-flex align-items-center gap-2 flex-shrink-0 flex-wrap">
                              <button
                                type="button"
                                onClick={() => applyPoiAutofill(selectedPoi, importOptions)}
                                className="btn btn-sm btn-primary-600 d-inline-flex align-items-center gap-1 px-14 py-8 radius-6 fw-semibold"
                              >
                                <Icon icon="solar:check-read-linear" />
                                <span>Autofill All</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => setShowCustomImport(!showCustomImport)}
                                className="btn btn-sm btn-outline-primary px-12 py-8 radius-6 d-inline-flex align-items-center gap-1"
                              >
                                <Icon icon="solar:settings-linear" />
                                <span>Options</span>
                              </button>
                              <button
                                type="button"
                                onClick={clearSelectedPoi}
                                className="btn btn-sm btn-outline-secondary px-10 py-8 radius-6"
                              >
                                Clear
                              </button>
                            </div>
                          </div>

                          {/* Custom Import Options Collapsible */}
                          {showCustomImport && (
                            <div className="mt-12 pt-12 border-top">
                              <div className="text-xs fw-bold text-secondary-light mb-8">Select fields to populate:</div>
                              <div className="d-flex flex-wrap gap-3">
                                {[
                                  { key: "name", label: "Name" },
                                  { key: "category", label: "Category" },
                                  { key: "address", label: "Address & Pin" },
                                  { key: "contact", label: "Phone & Website" },
                                  { key: "hours", label: "Opening Hours" },
                                  { key: "photos", label: "Photos" },
                                ].map((opt) => (
                                  <label key={opt.key} className="form-check-label d-flex align-items-center gap-1 text-xs cursor-pointer">
                                    <input
                                      type="checkbox"
                                      className="form-check-input m-0"
                                      checked={importOptions[opt.key]}
                                      onChange={(e) => setImportOptions(prev => ({ ...prev, [opt.key]: e.target.checked }))}
                                    />
                                    <span>{opt.label}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                  {/* FORM FIELDS */}
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-sm">
                      Location Name (Public) <span className="text-danger">*</span>
                    </label>
                    <input 
                      className="form-control" 
                      name="location_name" 
                      value={formData.location_name} 
                      onChange={handleChange} 
                      required 
                    />
                  </div>

                  <div className="col-md-6">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label className="form-label fw-semibold text-sm mb-0 d-flex align-items-center gap-2">
                        <span>Store Code <span className="text-danger">*</span></span>
                        {isCheckingStoreCode && (
                          <Icon icon="line-md:loading-twotone-loop" className="text-xs text-primary-600" />
                        )}
                      </label>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary py-0 px-8 radius-4 text-xs d-inline-flex align-items-center gap-1"
                        onClick={handleAutoGenerateStoreCode}
                        disabled={isGeneratingStoreCode}
                        title="Auto-generate unique Store Code"
                      >
                        <Icon icon={isGeneratingStoreCode ? "line-md:loading-twotone-loop" : "solar:magic-stick-3-bold"} className="text-sm" />
                        <span>Auto-generate</span>
                      </button>
                    </div>
                    <input 
                      className={`form-control ${storeCodeConflict?.exists ? "is-invalid border-danger bg-danger-50" : ""}`} 
                      name="store_code" 
                      value={formData.store_code || ""} 
                      onChange={handleChange} 
                      required 
                    />
                    {storeCodeConflict?.exists ? (
                      <div className="mt-8 p-10 radius-8 bg-danger-50 border border-danger-200 d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <div className="d-flex align-items-start gap-2 text-danger-600 text-xs">
                          <Icon icon="solar:danger-triangle-bold" className="text-base flex-shrink-0 mt-1" />
                          <div>
                            <span className="fw-bold">Store code already exists!</span>{" "}
                            Used by{" "}
                            <span className="fw-semibold">
                              "{storeCodeConflict.conflictingLocation?.location_name || `ID #${storeCodeConflict.conflictingLocation?.location_id}`}"
                            </span>.
                          </div>
                        </div>
                        {storeCodeConflict.suggestion && (
                          <button
                            type="button"
                            className="btn btn-xs btn-danger px-10 py-4 radius-6 text-xs fw-semibold d-inline-flex align-items-center gap-1 shadow-2xs"
                            onClick={() => handleApplyStoreCodeSuggestion(storeCodeConflict.suggestion)}
                          >
                            <Icon icon="solar:magic-stick-3-bold" className="text-sm" />
                            <span>Use suggestion: {storeCodeConflict.suggestion}</span>
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="form-text text-xs text-secondary-light">
                        Mandatory store identifier. If left empty, one will be generated automatically on save.
                      </div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-sm">Parent Top Location</label>
                    <select className="form-select" name="top_location_id" value={formData.top_location_id} onChange={handleChange}>
                      <option value="">-- None / Select Parent Top Location --</option>
                      {parentAreas?.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-sm d-flex align-items-center justify-content-between">
                      <span>Primary Category</span>
                      {presetCategories.length > 0 && (
                        <span className="text-warning-700 text-xs fw-normal d-inline-flex align-items-center gap-1">
                          <span className="text-warning-600">★</span> Presets prioritized
                        </span>
                      )}
                    </label>
                    <select 
                      className="form-select" 
                      name="primary_category_id" 
                      value={resolveCategoryValue(formData.primary_category_id)} 
                      onChange={handleChange}
                    >
                      <option value="">-- Select Primary Category --</option>
                      {formData.primary_category_id && !categoryOptions.some(c => c.label_de === resolveCategoryValue(formData.primary_category_id)) && (
                        <option value={formData.primary_category_id}>{formData.primary_category_id}</option>
                      )}
                      {presetCategories.length > 0 ? (
                        <>
                          <optgroup label={`★ Preset Categories (${selectedTopLocation?.name || "Top Location"})`}>
                            {presetCategories.map(cat => (
                              <option key={`preset-prim-${cat.id || cat.type_key}`} value={cat.label_de}>
                                ★ {cat.label_de} {cat.label ? `(${cat.label})` : ""}
                              </option>
                            ))}
                          </optgroup>
                          <optgroup label="All Other Categories">
                            {otherCategories.map(cat => (
                              <option key={cat.id || cat.type_key} value={cat.label_de}>
                                {cat.label_de}
                              </option>
                            ))}
                          </optgroup>
                        </>
                      ) : (
                        categoryOptions.map(cat => (
                          <option key={cat.id || cat.type_key} value={cat.label_de}>
                            {cat.label_de}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-sm">Opening Date</label>
                    <input type="date" className="form-control" name="opening_date" value={formData.opening_date} onChange={handleChange} />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-sm d-flex align-items-center justify-content-between">
                      <span>Secondary Category</span>
                      {presetCategories.length > 0 && (
                        <span className="text-warning-700 text-xs fw-normal d-inline-flex align-items-center gap-1">
                          <span className="text-warning-600">★</span> Presets prioritized
                        </span>
                      )}
                    </label>
                    <select 
                      className="form-select" 
                      name="additional_category" 
                      value={resolveCategoryValue(formData.additional_category)} 
                      onChange={handleChange}
                    >
                      <option value="">-- Select Secondary Category --</option>
                      {formData.additional_category && !categoryOptions.some(c => c.label_de === resolveCategoryValue(formData.additional_category)) && (
                        <option value={formData.additional_category}>{formData.additional_category}</option>
                      )}
                      {presetCategories.length > 0 ? (
                        <>
                          <optgroup label={`★ Preset Categories (${selectedTopLocation?.name || "Top Location"})`}>
                            {presetCategories.map(cat => (
                              <option key={`preset-sec-${cat.id || cat.type_key}`} value={cat.label_de}>
                                ★ {cat.label_de} {cat.label ? `(${cat.label})` : ""}
                              </option>
                            ))}
                          </optgroup>
                          <optgroup label="All Other Categories">
                            {otherCategories.map(cat => (
                              <option key={cat.id || cat.type_key} value={cat.label_de}>
                                {cat.label_de}
                              </option>
                            ))}
                          </optgroup>
                        </>
                      ) : (
                        categoryOptions.map(cat => (
                          <option key={cat.id || cat.type_key} value={cat.label_de}>
                            {cat.label_de}
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  <div className="col-12">
                    <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <label className="form-label fw-semibold text-sm mb-0">Public Description</label>
                        <span className="badge bg-primary-50 text-primary-600 border border-primary-200 text-xs px-2 py-0.5 rounded-pill d-inline-flex align-items-center gap-1">
                          <Icon icon="solar:magic-stick-3-bold" className="text-primary-600" />
                          Gemini AI
                        </span>
                      </div>

                      {/* AI Action Buttons */}
                      <div className="d-flex align-items-center flex-wrap gap-2 position-relative">
                        {previousDescription !== null && (
                          <button
                            type="button"
                            className="btn btn-xs btn-outline-neutral text-secondary-light py-1 px-8 radius-6 text-xs d-inline-flex align-items-center gap-1"
                            onClick={() => {
                              const reverted = previousDescription;
                              setPreviousDescription(formData.description);
                              setFormData(prev => ({ ...prev, description: reverted }));
                              showToast('info', 'Reverted description');
                            }}
                            title="Undo / Revert to previous text"
                          >
                            <Icon icon="solar:undo-left-round-linear" />
                            <span>Revert</span>
                          </button>
                        )}

                        {/* Primary AI Generate Button */}
                        <button
                          type="button"
                          className="btn btn-xs btn-primary-600 text-white py-1 px-10 radius-6 text-xs d-inline-flex align-items-center gap-1 fw-semibold shadow-xs"
                          disabled={aiLoading}
                          onClick={() => handleAiDescription('generate')}
                          title="Generate description automatically from location data"
                        >
                          {aiLoading && aiAction === 'generate' ? (
                            <>
                              <Icon icon="line-md:loading-twotone-loop" className="me-1" />
                              <span>Drafting...</span>
                            </>
                          ) : (
                            <>
                              <Icon icon="solar:stars-minimalistic-bold" />
                              <span>Generate with Gemini</span>
                            </>
                          )}
                        </button>

                        {/* Polish button (available when description exists) */}
                        {formData.description?.trim() && (
                          <button
                            type="button"
                            className="btn btn-xs btn-outline-primary text-primary-600 py-1 px-8 radius-6 text-xs d-inline-flex align-items-center gap-1 fw-medium"
                            disabled={aiLoading}
                            onClick={() => handleAiDescription('polish')}
                            title="Improve tone, flow, and grammar of current description"
                          >
                            {aiLoading && aiAction === 'polish' ? (
                              <>
                                <Icon icon="line-md:loading-twotone-loop" className="me-1" />
                                <span>Polishing...</span>
                              </>
                            ) : (
                              <>
                                <Icon icon="solar:magic-stick-linear" />
                                <span>Polish & Refine</span>
                              </>
                            )}
                          </button>
                        )}

                        {/* More AI Tools Dropdown Menu */}
                        <div className="position-relative">
                          <button
                            type="button"
                            className="btn btn-xs btn-outline-neutral text-secondary-light py-1 px-8 radius-6 text-xs d-inline-flex align-items-center gap-1"
                            onClick={() => setShowAiMenu(prev => !prev)}
                            disabled={aiLoading}
                            title="More AI options (shorten, translate)"
                          >
                            <Icon icon="solar:menu-dots-bold" />
                            <span>More</span>
                          </button>

                          {showAiMenu && (
                            <>
                              <div
                                className="position-fixed top-0 start-0 w-100 h-100"
                                style={{ zIndex: 1050 }}
                                onClick={() => setShowAiMenu(false)}
                              />
                              <div
                                className="position-absolute end-0 top-100 mt-1 bg-white border radius-8 shadow-lg py-2"
                                style={{ zIndex: 1055, minWidth: "220px" }}
                              >
                                <div className="px-3 py-1 text-xs text-muted fw-semibold border-bottom mb-1">
                                  Gemini Actions
                                </div>
                                <button
                                  type="button"
                                  className="w-100 text-start px-3 py-1.5 border-0 bg-transparent text-xs d-flex align-items-center gap-2 hover-bg-neutral-50"
                                  disabled={!formData.description?.trim() || aiLoading}
                                  onClick={() => {
                                    setShowAiMenu(false);
                                    handleAiDescription('shorten');
                                  }}
                                >
                                  <Icon icon="solar:minimize-square-linear" className="text-warning-600 text-sm" />
                                  <div>
                                    <div className="fw-medium">Make Shorter / Punchy</div>
                                    <small className="text-muted">Condense to 2-3 sentences</small>
                                  </div>
                                </button>
                                <button
                                  type="button"
                                  className="w-100 text-start px-3 py-1.5 border-0 bg-transparent text-xs d-flex align-items-center gap-2 hover-bg-neutral-50"
                                  disabled={!formData.description?.trim() || aiLoading}
                                  onClick={() => {
                                    setShowAiMenu(false);
                                    handleAiDescription('translate_en');
                                  }}
                                >
                                  <Icon icon="solar:global-linear" className="text-info-600 text-sm" />
                                  <div>
                                    <div className="fw-medium">Translate to English</div>
                                    <small className="text-muted">High-quality translation</small>
                                  </div>
                                </button>
                                <button
                                  type="button"
                                  className="w-100 text-start px-3 py-1.5 border-0 bg-transparent text-xs d-flex align-items-center gap-2 hover-bg-neutral-50"
                                  disabled={!formData.description?.trim() || aiLoading}
                                  onClick={() => {
                                    setShowAiMenu(false);
                                    handleAiDescription('translate_de');
                                  }}
                                >
                                  <Icon icon="solar:global-linear" className="text-success-600 text-sm" />
                                  <div>
                                    <div className="fw-medium">Translate to German</div>
                                    <small className="text-muted">Deutsche Übersetzung</small>
                                  </div>
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <textarea
                      className="form-control"
                      name="description"
                      rows="4"
                      value={formData.description}
                      onChange={handleChange}
                    ></textarea>

                    {aiLoading && (
                      <div className="d-flex align-items-center gap-2 mt-2 text-primary-600 text-xs bg-primary-50 px-3 py-2 radius-6 border border-primary-100">
                        <Icon icon="line-md:loading-twotone-loop" className="text-base" />
                        <span>Gemini is composing public description based on location details...</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* TAB 2: ADDRESS */}
              {activeTab === "Address" && (
                <div className="row g-4">
                  <div className="col-lg-5">
                    {/* Search Address Autocomplete */}
                    <label className="form-label fw-semibold text-sm">Address Lookup</label>
                    {isLoaded && (
                      <Autocomplete onLoad={a => (addressAutocompleteRef.current = a)} onPlaceChanged={onAddressChanged}>
                        <div className="position-relative mb-16">
                          <span className="position-absolute top-50 start-0 translate-middle-y ms-12 text-primary-600 text-lg pointer-events-none d-flex align-items-center">
                            <Icon icon="solar:magnifer-linear" />
                          </span>
                          <input className="form-control ps-36 radius-8 text-sm" placeholder="Search address to set pin..." />
                        </div>
                      </Autocomplete>
                    )}

                    <div className="row g-3">
                      <div className="col-8">
                        <label className="form-label fw-semibold text-xs">Street</label>
                        <input className="form-control form-control-sm" name="street_address" value={formData.street_address} onChange={handleChange} />
                      </div>
                      <div className="col-4">
                        <label className="form-label fw-semibold text-xs">Number</label>
                        <input className="form-control form-control-sm" name="house_number" value={formData.house_number} onChange={handleChange} />
                      </div>
                      <div className="col-6">
                        <label className="form-label fw-semibold text-xs">City</label>
                        <input className="form-control form-control-sm" name="city" value={formData.city} onChange={handleChange} />
                      </div>
                      <div className="col-6">
                        <label className="form-label fw-semibold text-xs">Postal Code</label>
                        <input className="form-control form-control-sm" name="postal_code" value={formData.postal_code} onChange={handleChange} />
                      </div>
                      <div className="col-6">
                        <label className="form-label fw-semibold text-xs">District / Region</label>
                        <input className="form-control form-control-sm" name="district" value={formData.district || ""} onChange={handleChange} />
                      </div>
                      <div className="col-6">
                        <label className="form-label fw-semibold text-xs">Country</label>
                        <input className="form-control form-control-sm" name="country" value={formData.country} onChange={handleChange} />
                      </div>
                    </div>

                    <div className="p-12 radius-8 bg-neutral-50 border mt-16">
                      <div className="d-flex align-items-center justify-content-between mb-8">
                        <span className="text-xs fw-bold text-secondary-light">Coordinates (Pin Position)</span>
                        <button
                          type="button"
                          className="btn btn-xs btn-link p-0 text-primary-600 text-xs text-decoration-none"
                          onClick={() => {
                            navigator.clipboard.writeText(`${formData.lat}, ${formData.lng}`);
                            showToast("success", "Coordinates copied to clipboard!");
                          }}
                        >
                          Copy Lat/Lng
                        </button>
                      </div>
                      <div className="row g-2">
                        <div className="col-6">
                          <input className="form-control form-control-sm bg-white" name="lat" value={formData.lat} onChange={handleChange} />
                        </div>
                        <div className="col-6">
                          <input className="form-control form-control-sm bg-white" name="lng" value={formData.lng} onChange={handleChange} />
                        </div>
                      </div>
                      <p className="text-xs text-secondary-light mt-8 mb-0">
                        💡 Tip: Drag the map pin to automatically reverse-geocode the address.
                      </p>
                    </div>
                  </div>

                  <div className="col-lg-7">
                    <div className="border radius-12 overflow-hidden shadow-sm h-100" style={{ minHeight: "420px" }}>
                      {isLoaded ? (
                        <GoogleMap 
                          onLoad={onMapLoad} 
                          mapContainerStyle={{ width: "100%", height: "100%" }} 
                          center={{ 
                            lat: parseFloat(formData.lat) || DEFAULT_CENTER.lat, 
                            lng: parseFloat(formData.lng) || DEFAULT_CENTER.lng 
                          }} 
                          zoom={formData.lat && formData.lng ? 16 : 12}
                          mapTypeId="hybrid"
                          options={{ mapTypeId: "hybrid", mapTypeControl: true }}
                        >
                          {formData.lat && formData.lng && (
                            <MarkerF 
                              position={{ lat: parseFloat(formData.lat), lng: parseFloat(formData.lng) }} 
                              draggable 
                              onDragEnd={handleMarkerDragEnd} 
                            />
                          )}
                        </GoogleMap>
                      ) : (
                        <div className="d-flex align-items-center justify-content-center h-100 bg-neutral-100 text-secondary-light">
                          Loading Google Maps...
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 3: BUILDING OUTLINE */}
              {activeTab === "Building Outline" && (() => {
                const allPolys = parsePolygons(formData.geometrics_outline);
                const activeIndex = (activePolyIndex < allPolys.length && activePolyIndex >= 0) ? activePolyIndex : 0;
                const currentPoints = allPolys[activeIndex] || [];

                return (
                  <div className="p-20 radius-12 bg-white border">
                    <div className="d-flex align-items-center justify-content-between mb-16 pb-12 border-bottom flex-wrap gap-3">
                      <div className="d-flex align-items-center gap-2">
                        <div className="w-32-px h-32-px bg-primary-50 text-primary-600 radius-8 d-flex align-items-center justify-content-center flex-shrink-0">
                          <Icon icon="solar:buildings-2-linear" className="text-lg" />
                        </div>
                        <div>
                          <h6 className="text-md fw-semibold mb-0 text-primary-light">Building Outline</h6>
                          <p className="text-xs text-secondary-light mb-0">Draw, refine, and manage the geographic boundary polygons and vertex points for this building.</p>
                        </div>
                      </div>

                      {/* Download Floorplan Template Image Button */}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary radius-8 d-inline-flex align-items-center gap-2 px-14 py-8 text-xs fw-semibold ms-auto"
                        onClick={() => setShowTemplateModal(true)}
                        disabled={!allPolys || allPolys.length === 0 || allPolys.every((p) => p.length < 3)}
                        title="Download a calibrated floorplan template image matching the building outline"
                      >
                        <Icon icon="solar:download-square-bold" className="text-base" />
                        <span>Download Template Image</span>
                      </button>
                    </div>

                    <div className="row g-4">
                      {/* Left Column: Coordinates Table & Controls */}
                      <div className="col-xl-5 col-lg-5">
                        <div className="p-20 radius-12 bg-white border h-100 d-flex flex-column justify-content-between overflow-hidden" style={{ minHeight: "580px" }}>
                          <div>
                            {/* Header Bar */}
                            <div className="d-flex align-items-center justify-content-between mb-16 pb-12 border-bottom flex-wrap gap-2">
                              <div className="d-flex align-items-center gap-2">
                                <span className="badge bg-primary-subtle text-primary-600 radius-6 px-10 py-6 text-xs fw-semibold">
                                  {currentPoints.length} {currentPoints.length === 1 ? "Vertex" : "Vertices"}
                                </span>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setShowOutlineImport((prev) => !prev)}
                                  className={`btn btn-xs ${
                                    showOutlineImport ? "btn-primary text-white" : "btn-outline-primary"
                                  } py-1 px-8 radius-4 text-xs d-inline-flex align-items-center gap-1`}
                                  title="Import coordinates from file or text"
                                >
                                  <Icon icon="solar:import-linear" />
                                  <span>Import</span>
                                </button>
                                {currentPoints.length > 0 && (
                                  <button
                                    type="button"
                                    onClick={handleClearBoundary}
                                    className="btn btn-xs btn-outline-danger py-1 px-8 radius-4 text-xs d-inline-flex align-items-center gap-1"
                                    title="Clear boundary"
                                  >
                                    <Icon icon="solar:trash-bin-trash-bold" />
                                    <span>Clear</span>
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Outline Import Panel (File or Text Field) */}
                            {showOutlineImport && (
                              <div className="mb-14 p-12 radius-8 bg-neutral-50 border">
                                <div className="d-flex align-items-center justify-content-between mb-8">
                                  <span className="text-xs fw-semibold text-dark d-inline-flex align-items-center gap-1">
                                    <Icon icon="solar:import-bold" className="text-primary-600" />
                                    Import Outline Coordinates
                                  </span>
                                  {allPolys.length > 0 && (
                                    <div className="btn-group btn-group-sm" role="group">
                                      <button
                                        type="button"
                                        className={`btn btn-xs py-0 px-6 text-xs ${
                                          outlineImportMode === "replace"
                                            ? "btn-primary text-white"
                                            : "btn-outline-secondary bg-white"
                                        }`}
                                        style={{ fontSize: "10px", height: "22px" }}
                                        onClick={() => setOutlineImportMode("replace")}
                                      >
                                        Replace
                                      </button>
                                      <button
                                        type="button"
                                        className={`btn btn-xs py-0 px-6 text-xs ${
                                          outlineImportMode === "append"
                                            ? "btn-primary text-white"
                                            : "btn-outline-secondary bg-white"
                                        }`}
                                        style={{ fontSize: "10px", height: "22px" }}
                                        onClick={() => setOutlineImportMode("append")}
                                      >
                                        + Add Shape
                                      </button>
                                    </div>
                                  )}
                                </div>

                                {/* Option 1: File Upload */}
                                <div className="mb-10">
                                  <input
                                    ref={outlineFileInputRef}
                                    type="file"
                                    accept=".json,.geojson,.kml,.csv,.txt"
                                    className="d-none"
                                    onChange={handleOutlineFileImport}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => outlineFileInputRef.current?.click()}
                                    className="btn btn-sm btn-outline-primary bg-white w-100 radius-6 py-6 px-10 text-xs fw-medium d-flex align-items-center justify-content-center gap-2 border-dashed"
                                  >
                                    <Icon icon="solar:upload-minimalistic-linear" className="text-sm" />
                                    <span>Select File (.json, .geojson, .kml, .csv, .txt)</span>
                                  </button>
                                </div>

                                <div className="text-center text-secondary-light mb-8" style={{ fontSize: "10px" }}>
                                  — OR PASTE COORDINATES / JSON —
                                </div>

                                {/* Option 2: Text Field */}
                                <textarea
                                  className="form-control radius-6 bg-white text-dark mb-8"
                                  rows={4}
                                  style={{ fontFamily: "monospace", fontSize: "11px" }}
                                  placeholder={'Paste JSON, GeoJSON, KML, or Lat/Lng lines:\ne.g. [{"lat": 48.13539, "lng": 11.69412}, ...]\nor 48.13539, 11.69412'}
                                  value={outlineImportText}
                                  onChange={(e) => setOutlineImportText(e.target.value)}
                                />

                                <div className="d-flex align-items-center justify-content-end gap-2">
                                  <button
                                    type="button"
                                    className="btn btn-xs btn-outline-secondary py-1 px-8 radius-4 text-xs"
                                    onClick={() => {
                                      setShowOutlineImport(false);
                                      setOutlineImportText("");
                                    }}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-xs btn-primary text-white py-1 px-10 radius-4 text-xs d-inline-flex align-items-center gap-1 fw-semibold"
                                    disabled={!outlineImportText.trim()}
                                    onClick={handleOutlineTextImport}
                                  >
                                    <Icon icon="solar:check-circle-bold" />
                                    <span>Import Text</span>
                                  </button>
                                </div>
                              </div>
                            )}

                          {/* Multi-Polygon Selector & Add Shape */}
                          {allPolys.length > 0 && (
                            <div className="d-flex align-items-center gap-2 mb-12 overflow-x-auto pb-1 flex-nowrap" style={{ scrollbarWidth: "thin" }}>
                              {allPolys.map((poly, pIdx) => {
                                const isPolyActive = activeIndex === pIdx;
                                return (
                                  <button
                                    key={pIdx}
                                    type="button"
                                    className={`btn btn-sm text-nowrap flex-shrink-0 d-inline-flex align-items-center justify-content-center gap-1 px-10 radius-6 text-xs fw-semibold ${
                                      isPolyActive
                                        ? "text-white shadow-xs"
                                        : "btn-outline-primary bg-white text-primary-600 border"
                                    }`}
                                    style={{
                                      height: "34px",
                                      minHeight: "34px",
                                      maxHeight: "34px",
                                      lineHeight: "1",
                                      whiteSpace: "nowrap",
                                      backgroundColor: isPolyActive ? "#f97316" : "#ffffff",
                                      borderColor: isPolyActive ? "#ea580c" : "#bfdbfe",
                                      color: isPolyActive ? "#ffffff" : "#2563eb",
                                      flexShrink: 0
                                    }}
                                    onClick={() => {
                                      setActivePolyIndex(pIdx);
                                      setSelectedVertexIndex(0);
                                    }}
                                  >
                                    <span
                                      className="radius-circle d-inline-block flex-shrink-0"
                                      style={{
                                        width: "7px",
                                        height: "7px",
                                        backgroundColor: isPolyActive ? "#ffffff" : "#0d6efd"
                                      }}
                                    />
                                    <span>Shape {pIdx + 1}</span>
                                    <span className="opacity-75 font-monospace">({poly.length} pts)</span>
                                  </button>
                                );
                              })}

                              {/* Listview Add Shape Button */}
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary text-nowrap flex-shrink-0 d-inline-flex align-items-center justify-content-center gap-1 px-10 radius-6 text-xs fw-semibold border-dashed"
                                style={{
                                  height: "34px",
                                  minHeight: "34px",
                                  maxHeight: "34px",
                                  lineHeight: "1",
                                  whiteSpace: "nowrap",
                                  flexShrink: 0
                                }}
                                onClick={handleAddShape}
                                title="Add an additional shape"
                              >
                                <Icon icon="solar:add-circle-linear" style={{ fontSize: "15px" }} />
                                <span>+ Add Shape</span>
                              </button>
                            </div>
                          )}

                          {/* Step Adjuster Controls */}
                          <div className="d-flex align-items-center mb-12 bg-neutral-50 p-8 radius-8 border flex-wrap gap-2">
                            <span className="text-xs fw-semibold text-secondary-light">Nudge Step:</span>
                            <div className="d-flex align-items-center gap-1">
                              {[
                                { label: "±1m", value: 0.00001, title: "±0.00001 deg (~1.1m)" },
                                { label: "±5m", value: 0.00005, title: "±0.00005 deg (~5.5m)" },
                                { label: "±10m", value: 0.0001, title: "±0.00010 deg (~11m)" },
                                { label: "±10cm", value: 0.000001, title: "±0.000001 deg (~11cm)" }
                              ].map((stepOpt) => (
                                <button
                                  key={stepOpt.value}
                                  type="button"
                                  title={stepOpt.title}
                                  className={`btn btn-xs py-1 px-8 radius-4 text-xs fw-medium ${
                                    coordStep === stepOpt.value
                                      ? "btn-primary shadow-xs"
                                      : "btn-outline-neutral text-secondary-light bg-white border"
                                  }`}
                                  onClick={() => setCoordStep(stepOpt.value)}
                                >
                                  {stepOpt.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Vertices Coordinates Table */}
                          {currentPoints.length > 0 ? (
                            <div className="table-responsive border radius-8" style={{ maxHeight: "360px", overflowY: "auto" }}>
                              <table className="table table-sm table-hover align-middle mb-0 text-xs">
                                <thead className="table-light sticky-top">
                                  <tr>
                                    <th className="text-center py-2 px-1 text-secondary-light fw-semibold" style={{ width: "38px" }}>#</th>
                                    <th className="py-2 px-2 text-secondary-light fw-semibold">Coordinates (Lat / Lng)</th>
                                    <th className="text-center py-2 px-1 text-secondary-light fw-semibold" style={{ width: "48px" }}>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {currentPoints.map((pt, ptIdx) => {
                                    const isSelected = selectedVertexIndex === ptIdx;
                                    return (
                                      <tr
                                        key={`pt-${ptIdx}`}
                                        className={isSelected ? "table-primary" : ""}
                                        onClick={() => {
                                          setSelectedVertexIndex(ptIdx);
                                          if (outlineMapRef.current && pt.lat && pt.lng) {
                                            outlineMapRef.current.panTo({ lat: Number(pt.lat), lng: Number(pt.lng) });
                                          }
                                        }}
                                        style={{ cursor: "pointer", transition: "background-color 0.15s ease" }}
                                      >
                                        <td className="text-center py-2 px-1 fw-bold align-middle">
                                          <span className={`badge radius-4 px-2 py-1 ${isSelected ? "bg-primary text-white" : "bg-neutral-100 text-dark"}`}>
                                            {ptIdx + 1}
                                          </span>
                                        </td>
                                        <td className="py-2 px-2" onClick={(e) => e.stopPropagation()}>
                                          {/* Latitude Row */}
                                          <div className="d-flex align-items-center gap-1 mb-1">
                                            <span className={`badge font-monospace py-1 px-1 fw-semibold text-center ${isSelected ? "bg-primary-subtle text-primary-700" : "bg-neutral-200 text-secondary-light"}`} style={{ width: "32px", fontSize: "9px" }}>
                                              LAT
                                            </span>
                                            <div className="input-group input-group-sm flex-nowrap flex-grow-1">
                                              <button
                                                type="button"
                                                className="btn btn-outline-secondary px-2 py-0 fw-bold d-flex align-items-center justify-content-center"
                                                style={{ fontSize: "13px", minWidth: "26px", height: "26px", lineHeight: "1" }}
                                                title={`Decrease latitude by ${coordStep}`}
                                                onClick={() => adjustCoordinate(activeIndex, ptIdx, 'lat', -coordStep)}
                                              >
                                                -
                                              </button>
                                              <input
                                                type="number"
                                                step="any"
                                                className="form-control form-control-sm px-1 text-center font-monospace"
                                                style={{ fontSize: "11px", height: "26px" }}
                                                value={pt.lat !== undefined ? pt.lat : ""}
                                                onChange={(e) => handleCoordinateChange(activeIndex, ptIdx, 'lat', e.target.value)}
                                                onBlur={() => handleCoordinateBlur(activeIndex, ptIdx, 'lat')}
                                              />
                                              <button
                                                type="button"
                                                className="btn btn-outline-secondary px-2 py-0 fw-bold d-flex align-items-center justify-content-center"
                                                style={{ fontSize: "13px", minWidth: "26px", height: "26px", lineHeight: "1" }}
                                                title={`Increase latitude by ${coordStep}`}
                                                onClick={() => adjustCoordinate(activeIndex, ptIdx, 'lat', coordStep)}
                                              >
                                                +
                                              </button>
                                            </div>
                                          </div>

                                          {/* Longitude Row */}
                                          <div className="d-flex align-items-center gap-1">
                                            <span className={`badge font-monospace py-1 px-1 fw-semibold text-center ${isSelected ? "bg-primary-subtle text-primary-700" : "bg-neutral-200 text-secondary-light"}`} style={{ width: "32px", fontSize: "9px" }}>
                                              LNG
                                            </span>
                                            <div className="input-group input-group-sm flex-nowrap flex-grow-1">
                                              <button
                                                type="button"
                                                className="btn btn-outline-secondary px-2 py-0 fw-bold d-flex align-items-center justify-content-center"
                                                style={{ fontSize: "13px", minWidth: "26px", height: "26px", lineHeight: "1" }}
                                                title={`Decrease longitude by ${coordStep}`}
                                                onClick={() => adjustCoordinate(activeIndex, ptIdx, 'lng', -coordStep)}
                                              >
                                                -
                                              </button>
                                              <input
                                                type="number"
                                                step="any"
                                                className="form-control form-control-sm px-1 text-center font-monospace"
                                                style={{ fontSize: "11px", height: "26px" }}
                                                value={pt.lng !== undefined ? pt.lng : ""}
                                                onChange={(e) => handleCoordinateChange(activeIndex, ptIdx, 'lng', e.target.value)}
                                                onBlur={() => handleCoordinateBlur(activeIndex, ptIdx, 'lng')}
                                              />
                                              <button
                                                type="button"
                                                className="btn btn-outline-secondary px-2 py-0 fw-bold d-flex align-items-center justify-content-center"
                                                style={{ fontSize: "13px", minWidth: "26px", height: "26px", lineHeight: "1" }}
                                                title={`Increase longitude by ${coordStep}`}
                                                onClick={() => adjustCoordinate(activeIndex, ptIdx, 'lng', coordStep)}
                                              >
                                                +
                                              </button>
                                            </div>
                                          </div>
                                        </td>
                                        <td className="text-center py-2 px-1 align-middle" onClick={(e) => e.stopPropagation()}>
                                          <div className="d-flex flex-column align-items-center justify-content-center gap-1">
                                            <button
                                              type="button"
                                              className={`btn btn-sm p-0 radius-4 d-flex align-items-center justify-content-center ${isSelected ? "btn-primary text-white" : "btn-outline-primary"}`}
                                              style={{ width: "24px", height: "24px" }}
                                              title="Pan map to this vertex"
                                              onClick={() => {
                                                setSelectedVertexIndex(ptIdx);
                                                if (outlineMapRef.current && pt.lat && pt.lng) {
                                                  outlineMapRef.current.panTo({ lat: Number(pt.lat), lng: Number(pt.lng) });
                                                  outlineMapRef.current.setZoom(19);
                                                }
                                              }}
                                            >
                                              <Icon icon="solar:map-point-wave-linear" style={{ fontSize: "13px" }} />
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-outline-danger p-0 radius-4 d-flex align-items-center justify-content-center"
                                              style={{ width: "24px", height: "24px" }}
                                              title="Delete this vertex"
                                              onClick={() => handleRemovePoint(activeIndex, ptIdx)}
                                            >
                                              <Icon icon="solar:trash-bin-trash-bold" style={{ fontSize: "13px" }} />
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="p-20 radius-8 bg-neutral-50 border text-center my-8">
                              <div className="w-44-px h-44-px bg-primary-50 text-primary-600 radius-circle d-flex align-items-center justify-content-center mx-auto mb-10">
                                <Icon icon="tabler:polygon" className="text-2xl" />
                              </div>
                              <h6 className="fw-semibold text-dark mb-4 text-sm">No Outline Points Yet</h6>
                              <p className="text-xs text-secondary-light mb-12" style={{ maxWidth: "320px", margin: "0 auto" }}>
                                Draw a boundary on the map using the polygon tool, or create an initial starter outline around the pin coordinates.
                              </p>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary radius-6 d-inline-flex align-items-center gap-2 py-1 px-12 text-xs"
                                onClick={() => handleAddPoint(0)}
                              >
                                <Icon icon="solar:square-academic-cap-2-bold" />
                                <span>Create Starter Outline Around Pin</span>
                              </button>
                            </div>
                          )}

                          {/* Quick Toolbar */}
                          <div className="d-flex align-items-center justify-content-between mt-12 pt-8 border-top">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary radius-6 d-inline-flex align-items-center gap-1 py-1 px-10 text-xs"
                              onClick={() => handleAddPoint(activeIndex)}
                            >
                              <Icon icon="solar:add-circle-linear" />
                              <span>Add Vertex</span>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-secondary-light text-decoration-none p-0 text-xs"
                              onClick={() => setShowJsonRaw(prev => !prev)}
                            >
                              <Icon icon={showJsonRaw ? "solar:eye-closed-linear" : "solar:eye-linear"} className="me-1" />
                              {showJsonRaw ? "Hide Raw JSON" : "View Raw JSON"}
                            </button>
                          </div>

                          {showJsonRaw && (
                            <div className="mt-10">
                              <textarea
                                className="form-control radius-8 bg-neutral-50 text-secondary-light"
                                readOnly
                                rows={4}
                                style={{ fontFamily: "monospace", fontSize: 10 }}
                                value={formData.geometrics_outline || ""}
                              />
                            </div>
                          )}
                        </div>

                        {/* Location Pin Status & Recenter */}
                        {formData.lat && formData.lng && (
                          <div className="mt-12 pt-8 border-top d-flex align-items-center justify-content-between text-xs text-secondary-light">
                            <span>Pin: {Number(formData.lat).toFixed(5)}, {Number(formData.lng).toFixed(5)}</span>
                            <div className="d-flex align-items-center gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  if (outlineMapRef.current) {
                                    outlineMapRef.current.panTo({ lat: Number(formData.lat), lng: Number(formData.lng) });
                                    outlineMapRef.current.setZoom(18);
                                  }
                                }}
                                className="btn btn-sm btn-link p-0 text-primary-600 text-decoration-none text-xs"
                              >
                                Center Pin
                              </button>
                              {currentPoints.length > 0 && (
                                <>
                                  <span className="text-neutral-300">|</span>
                                  <button
                                    type="button"
                                    onClick={() => fitMapToBoundary(formData.geometrics_outline)}
                                    className="btn btn-sm btn-link p-0 text-primary-600 text-decoration-none text-xs"
                                  >
                                    Fit Outline
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right Column: Interactive Map */}
                    <div className="col-xl-7 col-lg-7">
                      <div className="border radius-12 overflow-hidden shadow-sm h-100 d-flex flex-column" style={{ minHeight: "580px" }}>
                        {/* Map Header Toolbar with Save Button */}
                        <div className="d-flex align-items-center justify-content-end px-16 py-10 bg-white border-bottom gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={handleAddShape}
                            className="btn btn-primary text-white radius-6 d-inline-flex align-items-center gap-1 fw-semibold shadow-xs"
                            style={{ fontSize: "12px", height: "30px", padding: "0 10px", lineHeight: "28px" }}
                            title="Add an additional shape to the map"
                          >
                            <Icon icon="solar:add-circle-bold" style={{ fontSize: "15px" }} />
                            <span>+ Add Shape</span>
                          </button>
                          {formData.lat && formData.lng && (
                            <button
                              type="button"
                              onClick={() => {
                                if (outlineMapRef.current) {
                                  outlineMapRef.current.panTo({ lat: Number(formData.lat), lng: Number(formData.lng) });
                                  outlineMapRef.current.setZoom(18);
                                }
                              }}
                              className="btn btn-outline-secondary radius-6 d-inline-flex align-items-center gap-1"
                              style={{ fontSize: "12px", height: "30px", padding: "0 10px", lineHeight: "28px" }}
                              title="Center on address pin"
                            >
                              <Icon icon="solar:map-point-linear" style={{ fontSize: "14px" }} />
                              <span>Center Pin</span>
                            </button>
                          )}
                          {currentPoints.length > 0 && (
                            <button
                              type="button"
                              onClick={() => fitMapToBoundary(formData.geometrics_outline)}
                              className="btn btn-outline-secondary radius-6 d-inline-flex align-items-center gap-1"
                              style={{ fontSize: "12px", height: "30px", padding: "0 10px", lineHeight: "28px" }}
                              title="Fit map to boundary"
                            >
                              <Icon icon="solar:maximize-square-linear" style={{ fontSize: "14px" }} />
                              <span>Fit Outline</span>
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={handleSaveOutline}
                            disabled={saveLoading}
                            className={`btn radius-6 d-inline-flex align-items-center gap-1 fw-semibold ${
                              hasFormChanged() ? "btn-primary shadow-xs" : "btn-outline-primary"
                            }`}
                            style={{ fontSize: "12px", height: "30px", padding: "0 12px", lineHeight: "28px" }}
                            title="Save changes to boundary"
                          >
                            {saveLoading ? (
                              <Icon icon="line-md:loading-twotone-loop" style={{ fontSize: "14px" }} />
                            ) : (
                              <Icon icon="solar:diskette-bold" style={{ fontSize: "14px" }} />
                            )}
                            <span>Save Outline</span>
                          </button>
                        </div>

                        <div className="flex-grow-1 position-relative" style={{ minHeight: "530px" }}>
                          {isLoaded ? (
                            <GoogleMap
                              mapContainerStyle={{ width: "100%", height: "100%", minHeight: "530px" }}
                              center={{
                                lat: Number(formData.lat) || DEFAULT_CENTER.lat,
                                lng: Number(formData.lng) || DEFAULT_CENTER.lng
                              }}
                              zoom={18}
                              mapTypeId="satellite"
                              onLoad={onOutlineMapLoad}
                              options={{
                                mapTypeId: "satellite",
                                tilt: 0,
                                rotateControl: false,
                                zoomControl: true,
                                mapTypeControl: false,
                                streetViewControl: false,
                                fullscreenControl: true,
                                styles: [
                                  {
                                    featureType: "all",
                                    elementType: "labels",
                                    stylers: [{ visibility: "off" }]
                                  }
                                ]
                              }}
                            >
                              <DrawTools
                                key={outlineDrawVersion}
                                data={formData.geometrics_outline}
                                onChange={(json) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    geometrics_outline: json
                                  }))
                                }
                                onVertexSelect={(polyIdx, vertIdx) => {
                                  setActivePolyIndex(polyIdx);
                                  setSelectedVertexIndex(vertIdx);
                                }}
                                activePolyIndex={activeIndex}
                              />
                            </GoogleMap>
                          ) : (
                            <div
                              className="w-100 h-100 radius-12 bg-neutral-100 d-flex align-items-center justify-content-center flex-column"
                              style={{ minHeight: "530px", border: "2px dashed #e3e6ef" }}
                            >
                              <Icon icon="line-md:loading-twotone-loop" className="text-primary-600 text-4xl mb-12" />
                              <p className="fw-medium text-secondary-light">Loading Geofence Engine...</p>
                            </div>
                          )}
                        </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* TAB: LEVELS */}
              {activeTab === "Levels" && (() => {
                const allPolys = parsePolygons(formData.geometrics_outline);
                const activeIndex = (activePolyIndex < allPolys.length && activePolyIndex >= 0) ? activePolyIndex : 0;
                const currentPoints = allPolys[activeIndex] || [];

                return (
                  <div className="p-20 radius-12 bg-white border">
                    <div className="d-flex align-items-center justify-content-between mb-16 pb-12 border-bottom">
                      <div className="d-flex align-items-center gap-2">
                        <div className="w-32-px h-32-px bg-primary-50 text-primary-600 radius-8 d-flex align-items-center justify-content-center flex-shrink-0">
                          <Icon icon="solar:layers-linear" className="text-lg" />
                        </div>
                        <div>
                          <h6 className="text-md fw-semibold mb-0 text-primary-light">Levels & Floorplans</h6>
                          <p className="text-xs text-secondary-light mb-0">Specify the number of levels above and below ground and manage georeferenced floorplans.</p>
                        </div>
                      </div>
                    </div>

                    {/* Information Callout */}
                    <div className="alert alert-info d-flex align-items-start gap-2 py-10 px-14 mb-16 text-xs radius-8 border-0 bg-info-50 text-info-900">
                      <Icon icon="solar:info-circle-bold" className="text-info-600 text-lg flex-shrink-0 mt-2" />
                      <div>
                        <strong>Level System Notice:</strong> Ground floor (e.g. <strong>0</strong>, <strong>EG</strong>, <strong>GF</strong>) is counted as level <strong>0</strong> and counts toward the <strong>Levels Above Ground</strong> (e.g. Ground Floor + 3 upper floors = 4 levels above ground; 1 basement floor = 1 level below ground).
                      </div>
                    </div>

                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label fw-semibold text-sm mb-6">
                          Levels Above Ground
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-neutral-50 border-end-0 text-secondary-light">
                            <Icon icon="solar:arrow-up-linear" />
                          </span>
                          <select
                            className="form-select radius-8 border-start-0"
                            name="building_levels_above_ground"
                            value={formData.building_levels_above_ground || ""}
                            onChange={handleChange}
                          >
                            <option value="">Select levels above ground</option>
                            {Array.from({ length: 50 }, (_, i) => i + 1).map((lvl) => (
                              <option key={lvl} value={String(lvl)}>
                                {lvl === 1 ? "1 (Ground Floor)" : lvl}
                              </option>
                            ))}
                            {formData.building_levels_above_ground &&
                              !Array.from({ length: 50 }, (_, i) => String(i + 1)).includes(String(formData.building_levels_above_ground)) && (
                                <option value={formData.building_levels_above_ground}>
                                  {formData.building_levels_above_ground}
                                </option>
                            )}
                          </select>
                        </div>
                        <span className="text-xs text-secondary-light mt-4 d-block">
                          Ground floor (0) + upper floors
                        </span>
                      </div>

                      <div className="col-md-4">
                        <label className="form-label fw-semibold text-sm mb-6">
                          Levels Below Ground
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-neutral-50 border-end-0 text-secondary-light">
                            <Icon icon="solar:arrow-down-linear" />
                          </span>
                          <select
                            className="form-select radius-8 border-start-0"
                            name="building_levels_below_ground"
                            value={formData.building_levels_below_ground || ""}
                            onChange={handleChange}
                          >
                            <option value="">Select levels below ground</option>
                            <option value="0">0 (None)</option>
                            {Array.from({ length: 20 }, (_, i) => -(i + 1)).map((lvl) => (
                              <option key={lvl} value={String(lvl)}>
                                {lvl}
                              </option>
                            ))}
                            {formData.building_levels_below_ground &&
                              formData.building_levels_below_ground !== "0" &&
                              !Array.from({ length: 20 }, (_, i) => String(-(i + 1))).includes(String(formData.building_levels_below_ground)) && (
                                <option value={formData.building_levels_below_ground}>
                                  {formData.building_levels_below_ground}
                                </option>
                            )}
                          </select>
                        </div>
                        <span className="text-xs text-secondary-light mt-4 d-block">
                          Underground / basement levels (negative: -1, -2, etc.)
                        </span>
                      </div>

                      <div className="col-md-4">
                        <label className="form-label fw-semibold text-sm mb-6">
                          Level of Main Entrance
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-neutral-50 border-end-0 text-secondary-light">
                            <Icon icon="solar:login-2-linear" />
                          </span>
                          <select
                            className="form-select radius-8 border-start-0"
                            name="main_entrance_level"
                            value={formData.main_entrance_level || ""}
                            onChange={handleChange}
                          >
                            <option value="">Select main entrance level</option>
                            {/* Below ground levels */}
                            {(() => {
                              const belowVal = parseInt(formData.building_levels_below_ground, 10);
                              const minBelow = !isNaN(belowVal) && belowVal < 0 ? belowVal : -5;
                              const items = [];
                              for (let i = minBelow; i <= -1; i++) items.push(i);
                              return items.map((lvl) => (
                                <option key={lvl} value={String(lvl)}>
                                  {lvl}
                                </option>
                              ));
                            })()}
                            <option value="0">0 (Ground Floor / EG)</option>
                            {/* Above ground floors */}
                            {(() => {
                              const aboveVal = parseInt(formData.building_levels_above_ground, 10);
                              const maxAbove = !isNaN(aboveVal) && aboveVal > 1 ? aboveVal - 1 : 10;
                              const items = [];
                              for (let i = 1; i <= Math.min(maxAbove, 50); i++) items.push(i);
                              return items.map((lvl) => (
                                <option key={lvl} value={String(lvl)}>
                                  {lvl}
                                </option>
                              ));
                            })()}
                            {formData.main_entrance_level &&
                              formData.main_entrance_level !== "0" &&
                              !["0", ...Array.from({ length: 20 }, (_, i) => String(-(i + 1))), ...Array.from({ length: 50 }, (_, i) => String(i + 1))].includes(String(formData.main_entrance_level)) && (
                                <option value={formData.main_entrance_level}>
                                  {formData.main_entrance_level}
                                </option>
                            )}
                          </select>
                        </div>
                        <span className="text-xs text-secondary-light mt-4 d-block">
                          Floor level where the main entrance is located
                        </span>
                      </div>
                    </div>

                    {/* Floorplans for configured levels */}
                    <hr className="my-20" />
                    <LevelFloorplansManager
                      locationId={id}
                      buildingLevelsAbove={formData.building_levels_above_ground}
                      buildingLevelsBelow={formData.building_levels_below_ground}
                      buildingOutline={currentPoints}
                      locationCenter={{ lat: formData.lat, lng: formData.lng }}
                      onOpenTemplateModal={() => setShowTemplateModal(true)}
                    />
                  </div>
                );
              })()}

              {/* TAB: SOCIAL & WEB */}
              {activeTab === "Social & Web" && (
                <div className="row g-4">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-sm">Website URL</label>
                    <div className="input-group">
                      <span className="input-group-text bg-neutral-50"><Icon icon="solar:global-linear" /></span>
                      <input className="form-control" name="website_url" value={formData.website_url || ""} onChange={handleChange} />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-sm">Email</label>
                    <div className="input-group">
                      <span className="input-group-text bg-neutral-50"><Icon icon="solar:letter-linear" /></span>
                      <input type="email" className="form-control" name="email" value={formData.email || ""} onChange={handleChange} />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-sm">Phone Number</label>
                    <div className="input-group">
                      <select 
                        className="form-select" 
                        style={{ maxWidth: '35%' }}
                        name="phone_country_code"
                        value={formData.phone_country_code || ""}
                        onChange={handleChange}
                      >
                        <option value="">Code</option>
                        {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                      </select>
                      <input className="form-control" name="phone_number" value={formData.phone_number || ""} onChange={handleChange} />
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-sm">WhatsApp Chat</label>
                    <input className="form-control" name="whatsapp_chat" value={formData.whatsapp_chat || ""} onChange={handleChange} />
                  </div>

                  {['facebook_link', 'instagram_link', 'linkedin_link', 'youtube_link', 'tiktok_link', 'twitter_link'].map(key => (
                    <div className="col-md-6" key={key}>
                      <label className="form-label fw-semibold text-capitalize text-sm">{key.replace('_link', '')} URL</label>
                      <input className="form-control" name={key} value={formData[key] || ""} onChange={handleChange} />
                    </div>
                  ))}
                </div>
              )}

              {/* TAB 4: OPENING HOURS */}
              {activeTab === "Opening Hours" && (
                <div>
                  {/* Quick Preset Helpers */}
                  <div className="p-16 radius-12 bg-neutral-50 border mb-20">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-12">
                      <span className="text-xs fw-bold text-secondary-light">Quick Presets & Helpers:</span>
                      <button
                        type="button"
                        onClick={() => applyHoursPreset('copy_monday')}
                        className="btn btn-xs btn-outline-primary radius-6 d-inline-flex align-items-center gap-1"
                      >
                        <Icon icon="solar:copy-linear" />
                        <span>Copy Monday to Weekdays</span>
                      </button>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <button type="button" onClick={() => applyHoursPreset('office')} className="btn btn-sm btn-white border radius-6 text-xs fw-medium shadow-2xs">
                        🏢 Mon-Fri 09:00 - 18:00
                      </button>
                      <button type="button" onClick={() => applyHoursPreset('retail')} className="btn btn-sm btn-white border radius-6 text-xs fw-medium shadow-2xs">
                        🛍️ Mon-Sat 10:00 - 20:00
                      </button>
                      <button type="button" onClick={() => applyHoursPreset('247')} className="btn btn-sm btn-white border radius-6 text-xs fw-medium shadow-2xs">
                        ⏱️ Open 24 Hours
                      </button>
                      <button type="button" onClick={() => applyHoursPreset('clear')} className="btn btn-sm btn-outline-danger radius-6 text-xs">
                        Clear All
                      </button>
                    </div>
                  </div>

                  <div className="bg-neutral-50 p-24 radius-12 border">
                    <div className="row g-3">
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                        <div className="col-md-6 d-flex align-items-center gap-3 bg-white p-12 radius-8 border shadow-sm" key={day}>
                          <label className="fw-bold text-sm text-primary-600 mb-0 text-capitalize" style={{ width: "100px" }}>{day}</label>
                          <input 
                            className="form-control border-0 bg-neutral-50 text-sm" 
                            name={`hours_${day}`} 
                            value={formData[`hours_${day}`] || ""} 
                            onChange={handleChange} 
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 5, 6, 7: PARKING, ACCESSIBILITY, INCLUSIVITY */}
              {["Parking", "Accessibility", "Inclusivity"].includes(activeTab) && (
                <div className="row g-3">
                  {Object.keys(formData).filter(k => 
                    (activeTab === "Parking" && k.includes('parking')) || 
                    (activeTab === "Accessibility" && (k.includes('wheelchair') || k.includes('hearing') || k.includes('appointment'))) ||
                    (activeTab === "Inclusivity" && (k.includes('gender') || k.includes('lgbtq') || k.includes('transgender')))
                  ).map(key => (
                    <div className="col-md-6" key={key}>
                      <div className="d-flex align-items-center justify-content-between p-16 border radius-12 bg-neutral-50 hover-bg-white transition-all shadow-sm">
                        <span className="fw-semibold text-secondary-light text-sm text-capitalize">{key.replace(/_/g, ' ')}</span>
                        <div className="form-check form-switch p-0 m-0">
                          <input className="form-check-input ms-0 cursor-pointer" type="checkbox" name={key} checked={formData[key] === 1} onChange={handleChange} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* TAB 8: MEDIA & FILES */}
              {activeTab === "Media & Files" && (
                <div className="d-flex flex-column gap-4">
                  {id ? (
                    <LocationMediaExplorer locationId={id} locationData={formData} />
                  ) : (
                    <div className="alert alert-primary radius-10 p-16 d-flex align-items-center gap-3">
                      <Icon icon="solar:info-circle-bold" className="text-2xl text-primary-600 flex-shrink-0" />
                      <div>
                        <h6 className="text-sm fw-bold mb-1">New Location Creation</h6>
                        <p className="text-xs mb-0 text-secondary-dark">
                          Please save this location first using the <strong>Save Master Location</strong> button below. Once created, you can upload high-resolution images and videos directly to Google Cloud Storage.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* TAB 9: PUBLISHING */}
              {activeTab === "Publishing" && (
                <div className="row g-4">
                  <div className="col-12">
                    <div className="alert bg-primary-50 border border-primary-200 radius-12 p-16 mb-4 d-flex align-items-start gap-3">
                      <Icon icon="solar:share-circle-bold-duotone" className="text-2xl text-primary-600 flex-shrink-0 mt-1" />
                      <div>
                        <h6 className="text-sm fw-bold text-primary-light mb-1">External Directory Publishing Permissions</h6>
                        <p className="text-xs text-secondary-light mb-0">
                          Control whether this Main Location is approved for synchronization and publishing to external platforms. By default, publishing is disabled and must be explicitly enabled per location.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Google Business Profile Toggle Card */}
                  <div className="col-md-6">
                    <div
                      className={`p-20 radius-12 border transition-all h-100 d-flex flex-column justify-content-between ${
                        formData.publish_google_business === 1
                          ? "bg-success-50 border-success-300 shadow-sm"
                          : "bg-neutral-50 border-neutral-200"
                      }`}
                    >
                      <div className="d-flex align-items-start justify-content-between gap-3 mb-12">
                        <div className="d-flex align-items-center gap-3">
                          <div className="w-44-px h-44-px radius-10 bg-white border d-flex align-items-center justify-content-center shadow-2xs flex-shrink-0">
                            <Icon icon="logos:google-icon" className="text-xl" />
                          </div>
                          <div>
                            <h6 className="text-md fw-bold text-primary-light mb-1">Google Business Profile</h6>
                            <span
                              className={`badge px-8 py-4 radius-4 text-2xs ${
                                formData.publish_google_business === 1
                                  ? "bg-success-600 text-white"
                                  : "bg-neutral-200 text-neutral-700"
                              }`}
                            >
                              {formData.publish_google_business === 1 ? "Approved for Publishing" : "Not Approved (Disabled)"}
                            </span>
                          </div>
                        </div>
                        <div className="form-check form-switch p-0 m-0">
                          <input
                            className="form-check-input ms-0 cursor-pointer"
                            style={{ width: "44px", height: "22px" }}
                            type="checkbox"
                            name="publish_google_business"
                            checked={formData.publish_google_business === 1}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-secondary-light mb-0">
                        Allows this location to be exported and synchronized with Google Business Profile (Maps & Search). Only approved locations can be exported via single or batch GBP export.
                      </p>
                    </div>
                  </div>

                  {/* Apple Business Connect Toggle Card */}
                  <div className="col-md-6">
                    <div
                      className={`p-20 radius-12 border transition-all h-100 d-flex flex-column justify-content-between ${
                        formData.publish_apple_business === 1
                          ? "bg-success-50 border-success-300 shadow-sm"
                          : "bg-neutral-50 border-neutral-200"
                      }`}
                    >
                      <div className="d-flex align-items-start justify-content-between gap-3 mb-12">
                        <div className="d-flex align-items-center gap-3">
                          <div className="w-44-px h-44-px radius-10 bg-white border d-flex align-items-center justify-content-center shadow-2xs flex-shrink-0">
                            <Icon icon="ic:baseline-apple" className="text-2xl text-dark" />
                          </div>
                          <div>
                            <h6 className="text-md fw-bold text-primary-light mb-1">Apple Business Connect</h6>
                            <span
                              className={`badge px-8 py-4 radius-4 text-2xs ${
                                formData.publish_apple_business === 1
                                  ? "bg-success-600 text-white"
                                  : "bg-neutral-200 text-neutral-700"
                              }`}
                            >
                              {formData.publish_apple_business === 1 ? "Approved for Publishing" : "Not Approved (Disabled)"}
                            </span>
                          </div>
                        </div>
                        <div className="form-check form-switch p-0 m-0">
                          <input
                            className="form-check-input ms-0 cursor-pointer"
                            style={{ width: "44px", height: "22px" }}
                            type="checkbox"
                            name="publish_apple_business"
                            checked={formData.publish_apple_business === 1}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      <p className="text-xs text-secondary-light mb-0">
                        Marks this location as approved for publishing to Apple Business Connect (Apple Maps place cards, indoor maps, and showcase updates).
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 10: INTERNAL IDS */}
              {activeTab === "Internal IDs" && (
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <label className="form-label text-xs fw-bold mb-0 d-flex align-items-center gap-2">
                        <span>STORE CODE <span className="text-danger">*</span></span>
                        {isCheckingStoreCode && (
                          <Icon icon="line-md:loading-twotone-loop" className="text-xs text-primary-600" />
                        )}
                      </label>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary py-0 px-8 radius-4 text-xs d-inline-flex align-items-center gap-1"
                        onClick={handleAutoGenerateStoreCode}
                        disabled={isGeneratingStoreCode}
                        title="Auto-generate unique Store Code"
                      >
                        <Icon icon={isGeneratingStoreCode ? "line-md:loading-twotone-loop" : "solar:magic-stick-3-bold"} className="text-sm" />
                        <span>Auto-generate</span>
                      </button>
                    </div>
                    <input 
                      className={`form-control ${storeCodeConflict?.exists ? "is-invalid border-danger bg-danger-50" : "bg-neutral-50"}`} 
                      name="store_code" 
                      value={formData.store_code || ""} 
                      onChange={handleChange} 
                      required 
                    />
                    {storeCodeConflict?.exists ? (
                      <div className="mt-8 p-10 radius-8 bg-danger-50 border border-danger-200 d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <div className="d-flex align-items-start gap-2 text-danger-600 text-xs">
                          <Icon icon="solar:danger-triangle-bold" className="text-base flex-shrink-0 mt-1" />
                          <div>
                            <span className="fw-bold">Store code already exists!</span>{" "}
                            Used by{" "}
                            <span className="fw-semibold">
                              "{storeCodeConflict.conflictingLocation?.location_name || `ID #${storeCodeConflict.conflictingLocation?.location_id}`}"
                            </span>.
                          </div>
                        </div>
                        {storeCodeConflict.suggestion && (
                          <button
                            type="button"
                            className="btn btn-xs btn-danger px-10 py-4 radius-6 text-xs fw-semibold d-inline-flex align-items-center gap-1 shadow-2xs"
                            onClick={() => handleApplyStoreCodeSuggestion(storeCodeConflict.suggestion)}
                          >
                            <Icon icon="solar:magic-stick-3-bold" className="text-sm" />
                            <span>Use suggestion: {storeCodeConflict.suggestion}</span>
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="form-text text-xs text-secondary-light">
                        Mandatory store identifier. If left empty, one will be generated automatically on save.
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-xs fw-bold">INTERNAL SYSTEM ID</label>
                    <input className="form-control bg-neutral-50" name="internal_id" value={formData.internal_id} onChange={handleChange} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-xs fw-bold">GOOGLE PLACE ID</label>
                    <input className="form-control bg-neutral-50" name="google_place_id" value={formData.google_place_id} onChange={handleChange} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-xs fw-bold">GOOGLE MAPS URL</label>
                    <input className="form-control bg-neutral-50" name="google_link" value={formData.google_link} onChange={handleChange} />
                  </div>

                  {/* Quick Publishing Permissions inside Internal IDs */}
                  <div className="col-12 pt-12 border-top mt-16">
                    <label className="form-label text-xs fw-bold text-secondary-light mb-12 d-block">
                      PUBLISHING PERMISSIONS
                    </label>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="d-flex align-items-center justify-content-between p-16 border radius-12 bg-neutral-50">
                          <div className="d-flex align-items-center gap-2">
                            <Icon icon="logos:google-icon" className="text-lg" />
                            <div>
                              <div className="fw-semibold text-sm text-primary-light">Publish to Google Business Profile</div>
                              <div className="text-2xs text-secondary-light">Allow GBP export & synchronization</div>
                            </div>
                          </div>
                          <div className="form-check form-switch p-0 m-0">
                            <input
                              className="form-check-input ms-0 cursor-pointer"
                              type="checkbox"
                              name="publish_google_business"
                              checked={formData.publish_google_business === 1}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="d-flex align-items-center justify-content-between p-16 border radius-12 bg-neutral-50">
                          <div className="d-flex align-items-center gap-2">
                            <Icon icon="ic:baseline-apple" className="text-xl text-dark" />
                            <div>
                              <div className="fw-semibold text-sm text-primary-light">Publish to Apple Business Connect</div>
                              <div className="text-2xs text-secondary-light">Allow Apple Business publishing</div>
                            </div>
                          </div>
                          <div className="form-check form-switch p-0 m-0">
                            <input
                              className="form-check-input ms-0 cursor-pointer"
                              type="checkbox"
                              name="publish_apple_business"
                              checked={formData.publish_apple_business === 1}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* STICKY BOTTOM ACTION & NAVIGATION BAR */}
        <FloatingFormActions
          isDirty={hasFormChanged()}
          prevStep={prevTab}
          onPrev={prevTab ? () => setActiveTab(prevTab) : null}
          nextStep={nextTab}
          onNext={nextTab ? () => setActiveTab(nextTab) : null}
          cancelUrl="/locations-main"
          cancelText="Discard"
          saveText={id ? "Update Profile" : "Create Location"}
          loading={saveLoading}
        />
      </form>

      {/* VERSION HISTORY MODAL */}
      <LocationHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        historyData={historyData}
        isLoading={isHistoryLoading}
        onRestore={handleRestoreVersion}
      />

      {/* 3-WAY DATA COMPARISON MODAL */}
      <LocationCompareModal
        isOpen={showCompareModal}
        onClose={() => setShowCompareModal(false)}
        locationId={id}
      />

      {/* OUTLINE FLOORPLAN TEMPLATE MODAL */}
      {showTemplateModal && (
        <OutlineTemplateModal
          show={showTemplateModal}
          onClose={() => setShowTemplateModal(false)}
          allPolys={parsePolygons(formData.geometrics_outline)}
          activePolyIndex={activePolyIndex}
          locationName={formData.name || formData.location_name}
          storeCode={formData.store_code}
          city={formData.city}
        />
      )}
    </>
  );
};

export default LocationsMainFormPage;